import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronLeft, ClipboardList, FilePlus2, FileText, Mail, Pencil, Trash2, X } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useNotificationSound } from '../../hooks/useNotificationSound';
import { useAuth } from '@/components/Auth/AuthContext';
import { azureSqlService } from '@/services/azureSqlService.js';
import { getTaskWorkflowSortRank, isPaidRfiTask, matchesTaskStatusFilter, type TaskStatusFilter } from './taskWorkflow';
import { filterVisibleTasks, hasActiveTaskSearchQuery } from './taskListVisibility';
import { compareTaskNumbers } from './taskSort';
import { calculateTaskUrgencyScore } from './taskUrgency';
import PageHeader from '@/components/Layout/PageHeader';
import { useIsNarrowScreen } from '@/hooks/useMediaQuery';
import { getCompanyAiFocusDomId, matchesCompanyAiFocusRecord, readCompanyAiFocusTarget, readCompanyAiFollowUpSeed, readCompanyAiPeriodFilter } from '@/utils/companyAiNavigation';
import { hasDateRange, normalizeDateOnlyInput } from '@/utils/listDateFilters';
import { LIST_SEARCH_DEBOUNCE_MS } from '@/constants/search';
import { getPrimaryRoleLabel } from '@/utils/swaRole';
import { formatSuburbLabel } from '@/utils/suburbText';
import { formatUserFacingError } from '@/utils/userFacingErrors';
import { Button, Card, DetailActionMenu, DetailFieldList, EmptyState, FormGrid, FormGridItem, FormSection, Input, Select, StatusBadge, Textarea } from '@/ui/components';
import ListPaginationControls from '@/components/List/ListPaginationControls';
import EmailSendModal, { type EmailModalTarget } from '@/components/Email/EmailSendModal';
import '@/pages/dataGridFocus.css';
import './TaskList.css';
import AssistantInbox from '@/components/ProjectAssistant/AssistantInbox';

/*
 * Task workspace screen.
 *
 * Plain-English view:
 * - This file is both the task list and the main task create/edit workspace.
 * - It also contains the special RFI task behavior, including invoice generation and payment-related status display.
 * - The frontend expects the API to return already-joined project, assignee, and invoice context for each row.
 */
type TaskCategory = 'Admin' | 'Engineering' | 'Finance' | 'To Do' | 'Structural' | 'Civil';
type TaskType = 'Standard' | 'Inspection' | 'RFI';
type TaskPriority = 'Low' | 'Medium' | 'High' | 'Critical';
type TaskStatus = 'Pending' | 'In Progress' | 'Completed' | 'Overdue';
type TaskSortBy = 'urgency' | 'number' | 'due' | 'remaining' | 'priority' | 'status' | 'workflow' | 'assigned' | 'created';
type SortDirection = 'asc' | 'desc';
const LIST_PAGE_SIZE = 20;
const LIST_PAGE_SIZE_OPTIONS = [20, 50, 100];
const TASK_OLDEST_FIRST_STORAGE_KEY = 'pmapp.taskList.oldestFirst.v1';

type TaskRecord = {
  id: string;
  taskNumber: string;
  title: string;
  description: string;
  category: string;
  taskType: string;
  projectId: number | null;
  projectNumber: string;
  projectName: string;
  projectSuburb: string;
  projectCouncil: string;
  projectLocation: string;
  assignedTo: string;
  assignedToEmail: string;
  createdBy: string;
  priority: string;
  pricingAmount: number | null;
  pricingCurrency: string;
  rfiTicketNumber: string;
  responseEmail: string;
  linkedInvoiceId: number | null;
  linkedInvoiceNumber: string;
  linkedInvoiceSource: string;
  invoiceStatus: string;
  invoicePaidDate?: string;
  paymentStatus: string;
  stripePaymentUrl: string;
  stripePaymentReference: string;
  priorityCode: string;
  status: string;
  statusCode: string;
  createdDate?: string;
  startDate?: string;
  dueDate?: string;
  completedDate?: string;
  modifiedDate?: string;
  remainingDays: number | null;
  urgencyScore: number | null;
};

type ProjectOption = {
  projectId: number;
  projectCode: string;
  projectName: string;
  siteAddress: string;
  suburb: string;
  description: string;
};

type ReminderRecipientOption = {
  value: string;
  label: string;
  recipientName: string;
};

type AssigneeOption = {
  value: string;
  label: string;
};

type WorkflowUserOption = {
  userId: number | null;
  email: string;
  displayName: string;
  role: string | null;
  department: string | null;
  jobTitle: string | null;
  sourceTable: string | null;
};

type WorkflowUserFallback = {
  email: string;
  displayName: string;
};

type TaskFormState = {
  category: TaskCategory;
  taskType: TaskType;
  priority: TaskPriority;
  description: string;
  startDate: string;
  dueDate: string;
  assignedToEmail: string;
  pricingAmount: string;
  pricingCurrency: string;
  rfiTicketNumber: string;
  responseEmail: string;
};

type TaskEditState = {
  title: string;
  taskNumber: string;
  category: TaskCategory;
  taskType: TaskType;
  priority: TaskPriority;
  status: TaskStatus;
  description: string;
  startDate: string;
  dueDate: string;
  assignedToEmail: string;
  pricingAmount: string;
  pricingCurrency: string;
  rfiTicketNumber: string;
  responseEmail: string;
};

type TaskStatusUndoNotice = {
  taskId: string;
  taskNumber: string;
  previousStatus: TaskStatus;
};

const WILSON_EMAIL = 'wd@linfinityeng.com';
const INTERNAL_TASK_USER_FALLBACKS: WorkflowUserFallback[] = [
  { email: 'yl@linfinityeng.com', displayName: 'Yuan Lin' },
  { email: WILSON_EMAIL, displayName: 'Wilson Du' },
  { email: 'jp@linfinityeng.com', displayName: 'JP' },
];
const TASK_CATEGORY_OPTIONS: TaskCategory[] = ['Admin', 'Engineering', 'Finance', 'To Do', 'Structural', 'Civil'];
const TASK_TYPE_OPTIONS: TaskType[] = ['Standard', 'Inspection', 'RFI'];
const TASK_PRIORITY_OPTIONS: TaskPriority[] = ['Low', 'Medium', 'High', 'Critical'];
const TASK_STATUS_OPTIONS: TaskStatus[] = ['Pending', 'In Progress', 'Completed', 'Overdue'];

function readTaskOldestFirstPreference(): boolean {
  if (typeof window === 'undefined') {
    return true;
  }

  try {
    const stored = window.localStorage.getItem(TASK_OLDEST_FIRST_STORAGE_KEY);
    if (stored === null) {
      return true;
    }
    return stored !== 'false';
  } catch {
    return true;
  }
}

function writeTaskOldestFirstPreference(value: boolean) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(TASK_OLDEST_FIRST_STORAGE_KEY, value ? 'true' : 'false');
  } catch {
    // Ignore storage failures; the checkbox still works for this page session.
  }
}

function todayYmd() {
  return new Date().toISOString().slice(0, 10);
}

function addDaysYmd(baseDate: string, days: number) {
  const date = new Date(`${baseDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function getDefaultTaskDueDate(startDate: string, taskType: TaskType) {
  const normalizedStartDate = String(startDate || '').trim() || todayYmd();
  return addDaysYmd(normalizedStartDate, taskType === 'RFI' ? 2 : 7);
}

function shouldAutoUpdateTaskDueDate(currentDueDate: string, startDate: string, taskType: TaskType) {
  const normalizedDueDate = String(currentDueDate || '').trim();
  if (!normalizedDueDate) {
    return true;
  }
  return normalizedDueDate === getDefaultTaskDueDate(startDate, taskType);
}

function defaultAssigneeForCategory(category: TaskCategory, currentUserEmail: string) {
  if (category === 'Civil') {
    return WILSON_EMAIL;
  }
  return currentUserEmail;
}

function createInitialForm(currentUserEmail: string): TaskFormState {
  return {
    category: 'To Do',
    taskType: 'Standard',
    priority: 'Medium',
    description: '',
    startDate: todayYmd(),
    dueDate: getDefaultTaskDueDate(todayYmd(), 'Standard'),
    assignedToEmail: defaultAssigneeForCategory('To Do', currentUserEmail),
    pricingAmount: '',
    pricingCurrency: 'AUD',
    rfiTicketNumber: '',
    responseEmail: '',
  };
}

function normalizeTaskCategory(value?: string): TaskCategory {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'admin') return 'Admin';
  if (normalized === 'engineering') return 'Engineering';
  if (normalized === 'finance') return 'Finance';
  if (normalized === 'civil') return 'Civil';
  if (normalized === 'structural' || normalized === 'structure') return 'Structural';
  return 'To Do';
}

function normalizeTaskStatus(value?: string): TaskStatus {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'pending' || normalized === 'todo' || normalized === 'to do' || normalized === 'to-do') return 'Pending';
  if (normalized === 'completed' || normalized === 'complete' || normalized === 'done') return 'Completed';
  if (normalized === 'in progress' || normalized === 'progress' || normalized === 'ongoing') return 'In Progress';
  if (normalized === 'overdue') return 'Overdue';
  return 'Pending';
}

function normalizeTaskPriority(value?: string): TaskPriority {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'low') return 'Low';
  if (normalized === 'high') return 'High';
  if (normalized === 'critical') return 'Critical';
  return 'Medium';
}

function normalizeTaskType(value?: string): TaskType {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'rfi') return 'RFI';
  if (normalized === 'inspection' || normalized === 'inspect') return 'Inspection';
  return 'Standard';
}

function stripSystemTaskPrefix(value?: string | null): string {
  return String(value || '').replace(/^\s*AI:\s*/i, '').trim();
}

function createInitialEditForm(task: TaskRecord, currentUserEmail: string): TaskEditState {
  const category = normalizeTaskCategory(task.category);
  return {
    title: stripSystemTaskPrefix(task.title || task.description || ''),
    taskNumber: task.taskNumber || '',
    category,
    taskType: normalizeTaskType(task.taskType),
    priority: normalizeTaskPriority(task.priority),
    status: normalizeTaskStatus(task.status),
    description: stripSystemTaskPrefix(task.description || task.title || ''),
    startDate: task.startDate ? String(task.startDate).slice(0, 10) : todayYmd(),
    dueDate: task.dueDate ? String(task.dueDate).slice(0, 10) : '',
    assignedToEmail: task.assignedToEmail || defaultAssigneeForCategory(category, currentUserEmail),
    pricingAmount: task.pricingAmount != null ? String(task.pricingAmount) : '',
    pricingCurrency: task.pricingCurrency || 'AUD',
    rfiTicketNumber: task.rfiTicketNumber || '',
    responseEmail: task.responseEmail || '',
  };
}

function normalizeComparableText(value?: string) {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function parseTaskIdSearchParams(params: URLSearchParams): string[] {
  const rawValues = [
    ...params.getAll('completeTaskIds'),
    ...params.getAll('completeTaskId'),
  ];
  const ids = rawValues
    .flatMap((value) => String(value || '').split(/[,\s;|]+/))
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0)
    .map((value) => String(Math.trunc(value)));

  return Array.from(new Set(ids));
}

function isValidOptionalEmail(value?: string) {
  const normalized = String(value || '').trim();
  return !normalized || /^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(normalized);
}

function mergeWorkflowUserOptions(primary: WorkflowUserOption[], fallbacks: WorkflowUserFallback[]): WorkflowUserOption[] {
  const merged = new Map<string, WorkflowUserOption>();

  primary.forEach((option) => {
    const normalizedEmail = String(option?.email || '').trim().toLowerCase();
    if (!normalizedEmail) {
      return;
    }

    merged.set(normalizedEmail, {
      ...option,
      email: normalizedEmail,
      displayName: String(option.displayName || normalizedEmail).trim() || normalizedEmail,
    });
  });

  fallbacks.forEach((fallback) => {
    const normalizedEmail = String(fallback?.email || '').trim().toLowerCase();
    if (!normalizedEmail || merged.has(normalizedEmail)) {
      return;
    }

    merged.set(normalizedEmail, {
      userId: null,
      email: normalizedEmail,
      displayName: String(fallback.displayName || normalizedEmail).trim() || normalizedEmail,
      role: null,
      department: null,
      jobTitle: null,
      sourceTable: 'task_fallback',
    });
  });

  return Array.from(merged.values()).sort((a, b) =>
    a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' })
  );
}

function isDuplicateProjectText(value: string, existing: string[]) {
  const normalized = normalizeComparableText(value);
  if (!normalized) return true;
  return existing.some((entry) => normalizeComparableText(entry) === normalized);
}

function buildProjectSearchValue(project: Pick<ProjectOption, 'projectCode' | 'projectName'>) {
  return [project.projectCode, project.projectName].filter(Boolean).join(' - ');
}

function normalizeProjectSearchResults(response: unknown): ProjectOption[] {
  const rows = Array.isArray(response)
    ? response
    : (
        response
        && typeof response === 'object'
        && ('ProjectId' in response || 'Id' in response || 'ProjectCode' in response || 'ProjectName' in response)
      )
      ? [response]
      : [];

  return rows.flatMap((project: any) => {
    const projectId = Number(project?.ProjectId ?? project?.Id);
    if (!Number.isFinite(projectId) || projectId <= 0) {
      return [];
    }

    return [{
      projectId,
      projectCode: String(project?.ProjectCode || '').trim(),
      projectName: String(project?.ProjectName || project?.Title || 'Unnamed project').trim() || 'Unnamed project',
      siteAddress: String(project?.SiteAddress || project?.Address || '').trim(),
      suburb: formatSuburbLabel(project?.Suburb || ''),
      description: String(project?.Description || '').trim(),
    }];
  });
}

function buildProjectLocationLabel(siteAddress?: string, suburb?: string, fallback?: string) {
  const normalizedSuburb = formatSuburbLabel(suburb);
  const parts: string[] = [];
  for (const value of [siteAddress, normalizedSuburb]) {
    const trimmed = String(value || '').trim();
    if (!trimmed || isDuplicateProjectText(trimmed, parts)) continue;
    parts.push(trimmed);
  }
  if (parts.length > 0) {
    return parts.join(', ');
  }
  return String(fallback || '').trim() || 'No address available';
}

function buildTaskProjectSummary(task: Pick<TaskRecord, 'projectNumber' | 'projectName' | 'projectSuburb' | 'projectLocation'>) {
  const summaryParts: string[] = [];
  for (const value of [task.projectNumber, task.projectName, task.projectSuburb, task.projectLocation]) {
    const trimmed = String(value || '').trim();
    if (!trimmed || isDuplicateProjectText(trimmed, summaryParts)) continue;
    summaryParts.push(trimmed);
  }
  return summaryParts.join(' • ');
}

function formatTaskPersonLabel(value?: string) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  if (trimmed.includes('@')) {
    return trimmed.split('@')[0].trim().toUpperCase();
  }
  return trimmed;
}

function resolveTaskAssigneeLabel(task: TaskRecord, editAssignedToEmail?: string | null) {
  const activeAssignedToEmail = String(editAssignedToEmail || '').trim();
  if (activeAssignedToEmail) {
    return formatTaskPersonLabel(activeAssignedToEmail) || 'Unassigned';
  }

  const canonicalAssignedToEmail = String(task.assignedToEmail || '').trim();
  if (canonicalAssignedToEmail) {
    return formatTaskPersonLabel(canonicalAssignedToEmail) || 'Unassigned';
  }

  return formatTaskPersonLabel(task.assignedTo || 'Unassigned') || 'Unassigned';
}

function formatTaskMoney(amount?: number | null, currency: string = 'AUD') {
  const value = Number(amount ?? 0);
  if (!Number.isFinite(value) || value <= 0) return '';
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: currency || 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

function buildTaskProjectChips(task: Pick<TaskRecord, 'projectNumber' | 'projectName' | 'projectSuburb'>) {
  const chips: string[] = [];
  for (const value of [task.projectNumber, task.projectName, task.projectSuburb]) {
    const trimmed = String(value || '').trim();
    if (!trimmed || isDuplicateProjectText(trimmed, chips)) continue;
    chips.push(trimmed);
  }
  return chips;
}

function buildEditableProjectFromTask(task: TaskRecord): ProjectOption | null {
  if (!task.projectId) {
    return null;
  }

  return {
    projectId: task.projectId,
    projectCode: task.projectNumber || '',
    projectName: task.projectName || '',
    siteAddress: task.projectLocation || '',
    suburb: task.projectSuburb || '',
    description: task.projectLocation || task.projectName || '',
  };
}

function getDefaultSortDirection(sortBy: TaskSortBy): SortDirection {
  if (sortBy === 'workflow') {
    return 'asc';
  }
  return 'desc';
}

function toSortableTaskAgeTime(value?: string | null): number {
  if (!value) {
    return Number.MAX_SAFE_INTEGER;
  }

  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : Number.MAX_SAFE_INTEGER;
}

function compareOldestTasksFirst(a: TaskRecord, b: TaskRecord): number {
  const aTimestamp = toSortableTaskAgeTime(a.createdDate || a.startDate || a.modifiedDate || a.dueDate);
  const bTimestamp = toSortableTaskAgeTime(b.createdDate || b.startDate || b.modifiedDate || b.dueDate);
  if (aTimestamp !== bTimestamp) {
    return aTimestamp - bTimestamp;
  }

  const taskNumberComparison = compareTaskNumbers(a.taskNumber, b.taskNumber);
  if (taskNumberComparison !== 0) {
    return taskNumberComparison;
  }

  return String(a.id || '').localeCompare(String(b.id || ''), 'en-AU', { numeric: true, sensitivity: 'base' });
}

const TaskList: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { taskId: taskDetailIdParam } = useParams<{ taskId?: string }>();
  const taskDetailId = String(taskDetailIdParam || '').trim();
  const isTaskDetailMode = Boolean(taskDetailId);
  const [tasks, setTasks] = useState<TaskRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [formNotice, setFormNotice] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const previousSearchActiveRef = useRef(false);
  const forceTaskRefreshRef = useRef(false);
  const handledEmailCompleteRef = useRef<string | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(LIST_PAGE_SIZE);
  const [pagination, setPagination] = useState({ page: 1, pageSize: LIST_PAGE_SIZE, totalRecords: 0, totalPages: 1 });
  const [statusFilter, setStatusFilter] = useState<TaskStatusFilter>('all');
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const [oldestFirst, setOldestFirst] = useState(readTaskOldestFirstPreference);
  const [sortBy, setSortBy] = useState<TaskSortBy>('number');
  const [sortDirection, setSortDirection] = useState<SortDirection>(getDefaultSortDirection('number'));
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<TaskFormState>({
    category: 'To Do',
    taskType: 'Standard',
    priority: 'Medium',
    description: '',
    startDate: todayYmd(),
    dueDate: getDefaultTaskDueDate(todayYmd(), 'Standard'),
    assignedToEmail: '',
    pricingAmount: '',
    pricingCurrency: 'AUD',
    rfiTicketNumber: '',
  });
  const [assigneeDirty, setAssigneeDirty] = useState(false);
  const [availableAssignees, setAvailableAssignees] = useState<WorkflowUserOption[]>([]);
  const [assigneeOptionsBusy, setAssigneeOptionsBusy] = useState(false);
  const [assigneeOptionsMessage, setAssigneeOptionsMessage] = useState<string | null>(null);
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [projectSearchLoading, setProjectSearchLoading] = useState(false);
  const [projectSearchError, setProjectSearchError] = useState<string | null>(null);
  const [projectOptions, setProjectOptions] = useState<ProjectOption[]>([]);
  const [descriptionMatchLoading, setDescriptionMatchLoading] = useState(false);
  const [descriptionMatchOptions, setDescriptionMatchOptions] = useState<ProjectOption[]>([]);
  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(null);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<TaskEditState | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editNotice, setEditNotice] = useState<string | null>(null);
  const [taskStatusUndoNotice, setTaskStatusUndoNotice] = useState<TaskStatusUndoNotice | null>(null);
  const [editProjectSearchQuery, setEditProjectSearchQuery] = useState('');
  const [editProjectSearchLoading, setEditProjectSearchLoading] = useState(false);
  const [editProjectSearchError, setEditProjectSearchError] = useState<string | null>(null);
  const [editProjectOptions, setEditProjectOptions] = useState<ProjectOption[]>([]);
  const [editSelectedProject, setEditSelectedProject] = useState<ProjectOption | null>(null);
  const [quickUpdateTaskId, setQuickUpdateTaskId] = useState<string | null>(null);
  const [generatingInvoiceTaskId, setGeneratingInvoiceTaskId] = useState<string | null>(null);
  const [deletingTaskId, setDeletingTaskId] = useState<string | null>(null);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderRecipientEmail, setReminderRecipientEmail] = useState('');
  const [reminderSending, setReminderSending] = useState(false);
  const [reminderError, setReminderError] = useState<string | null>(null);
  const [reminderNotice, setReminderNotice] = useState<string | null>(null);
  const [emailTarget, setEmailTarget] = useState<EmailModalTarget | null>(null);
  const isNarrowViewport = useIsNarrowScreen(640);
  const isMobileCreateMode = isNarrowViewport && formOpen;
  const { playSuccess, playError } = useNotificationSound();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();

  const currentUserEmail = (user?.userDetails || '').trim().toLowerCase();
  const aiFocus = readCompanyAiFocusTarget(location.state, 'tasks');
  const aiPeriodFilter = readCompanyAiPeriodFilter(location.state, 'tasks');
  const aiFollowUp = readCompanyAiFollowUpSeed(location.state);
  const hasActiveDateRange = hasDateRange(dateFrom, dateTo);
  const activeDateBasis = aiPeriodFilter?.dateBasis || 'due date';
  const isSearchActive = hasActiveTaskSearchQuery(searchQuery);
  const isUsingAiPeriodRange = Boolean(
    aiPeriodFilter
    && normalizeDateOnlyInput(aiPeriodFilter.startDate) === dateFrom
    && normalizeDateOnlyInput(aiPeriodFilter.endDate) === dateTo
  );

  const roleLabel = useMemo(
    () => getPrimaryRoleLabel(user?.userRoles, user?.userDetails),
    [user]
  );

  const assigneeOptions = useMemo(() => {
    const options = new Map<string, AssigneeOption>();
    const addOption = (email?: string | null, displayName?: string | null) => {
      const normalizedEmail = String(email || '').trim().toLowerCase();
      if (!normalizedEmail || options.has(normalizedEmail)) {
        return;
      }
      const trimmedName = String(displayName || '').trim();
      options.set(normalizedEmail, {
        value: normalizedEmail,
        label: trimmedName && normalizeComparableText(trimmedName) !== normalizedEmail
          ? `${trimmedName} (${normalizedEmail})`
          : normalizedEmail,
      });
    };

    availableAssignees.forEach((option) => addOption(option.email, option.displayName));
    addOption(currentUserEmail, user?.userDetails || 'Myself');
    addOption(form.assignedToEmail, form.assignedToEmail);
    addOption(editForm?.assignedToEmail, editForm?.assignedToEmail);

    return Array.from(options.values());
  }, [availableAssignees, currentUserEmail, editForm?.assignedToEmail, form.assignedToEmail, user?.userDetails]);

  const reminderRecipientOptions = useMemo(() => {
    const options = new Map<string, ReminderRecipientOption>();
    const addOption = (email?: string, name?: string) => {
      const normalizedEmail = String(email || '').trim().toLowerCase();
      if (!normalizedEmail || normalizedEmail === 'unassigned') {
        return;
      }

      const trimmedName = String(name || '').trim();
      const recipientName = trimmedName && normalizeComparableText(trimmedName) !== normalizedEmail
        ? trimmedName
        : normalizedEmail;
      const label = trimmedName && normalizeComparableText(trimmedName) !== normalizedEmail
        ? `${trimmedName} (${normalizedEmail})`
        : normalizedEmail;

      options.set(normalizedEmail, {
        value: normalizedEmail,
        label,
        recipientName,
      });
    };

    addOption(currentUserEmail, user?.userDetails || 'Myself');
    assigneeOptions.forEach((option) => {
      const cleanedLabel = option.label.replace(/\s*\([^)]+\)\s*$/, '').replace(/^Myself$/i, String(user?.userDetails || '').trim() || 'Myself');
      addOption(option.value, cleanedLabel);
    });
    tasks.forEach((task) => addOption(task.assignedToEmail, task.assignedTo));

    return Array.from(options.values());
  }, [assigneeOptions, currentUserEmail, tasks, user?.userDetails]);

  useEffect(() => {
    setForm(createInitialForm(currentUserEmail));
    setAssigneeDirty(false);
  }, [currentUserEmail]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !user) {
      setAvailableAssignees([]);
      setAssigneeOptionsBusy(false);
      setAssigneeOptionsMessage(null);
      return;
    }

    let active = true;
    const loadAssignees = async () => {
      try {
        setAssigneeOptionsBusy(true);
        const response = await azureSqlService.getWorkflowUserOptions();
        if (!active) return;

        const rows = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        const normalized = rows
          .map((row: any) => {
            const email = String(row?.email || row?.Email || '').trim().toLowerCase();
            if (!email) return null;
            return {
              userId: Number.isFinite(Number(row?.userId ?? row?.UserId)) ? Number(row?.userId ?? row?.UserId) : null,
              email,
              displayName: String(row?.displayName || row?.DisplayName || email).trim() || email,
              role: String(row?.role || row?.Role || '').trim() || null,
              department: String(row?.department || row?.Department || '').trim() || null,
              jobTitle: String(row?.jobTitle || row?.JobTitle || '').trim() || null,
              sourceTable: String(row?.sourceTable || row?.SourceTable || '').trim() || null,
            } as WorkflowUserOption;
          })
          .filter((row: WorkflowUserOption | null): row is WorkflowUserOption => Boolean(row));

        const mergedOptions = mergeWorkflowUserOptions(normalized, INTERNAL_TASK_USER_FALLBACKS);
        setAvailableAssignees(mergedOptions);
        setAssigneeOptionsMessage(
          normalized.length > 0
            ? null
            : 'Active users were limited in Users/UserList, so the internal team fallback is being shown.'
        );
      } catch (err: any) {
        if (!active) return;
        setAvailableAssignees(mergeWorkflowUserOptions([], INTERNAL_TASK_USER_FALLBACKS));
        setAssigneeOptionsMessage(err?.message || 'Active user lookup is unavailable, so the internal team fallback is being shown.');
      } finally {
        if (active) {
          setAssigneeOptionsBusy(false);
        }
      }
    };

    void loadAssignees();
    return () => {
      active = false;
    };
  }, [authLoading, isAuthenticated, user]);

  useEffect(() => {
    if (reminderRecipientOptions.length === 0) {
      if (reminderRecipientEmail) {
        setReminderRecipientEmail('');
      }
      return;
    }

    if (!reminderRecipientOptions.some((option) => option.value === reminderRecipientEmail)) {
      setReminderRecipientEmail(reminderRecipientOptions[0].value);
    }
  }, [reminderRecipientEmail, reminderRecipientOptions]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }
    const forceRefresh = forceTaskRefreshRef.current;
    forceTaskRefreshRef.current = false;
    const timer = window.setTimeout(() => {
      void loadTasks({
        query: isTaskDetailMode ? '' : searchQuery.trim(),
        ...(forceRefresh ? { useCache: false } : {}),
      }).catch(() => undefined);
    }, !isTaskDetailMode && searchQuery.trim() ? LIST_SEARCH_DEBOUNCE_MS : 0);

    return () => window.clearTimeout(timer);
  }, [authLoading, isAuthenticated, user, searchQuery, dateFrom, dateTo, aiPeriodFilter?.startDate, aiPeriodFilter?.endDate, aiPeriodFilter?.dateBasis, oldestFirst, sortBy, page, pageSize, isTaskDetailMode, taskDetailId]);

  useEffect(() => {
    if (authLoading || !isAuthenticated || !user) {
      return;
    }

    const params = new URLSearchParams(location.search);
    const taskIds = parseTaskIdSearchParams(params);
    if (taskIds.length === 0) {
      return;
    }

    const intentKey = taskIds.join(',');
    if (handledEmailCompleteRef.current === intentKey) {
      return;
    }
    handledEmailCompleteRef.current = intentKey;

    let cancelled = false;
    const completeTaskFromEmail = async () => {
      setQuickUpdateTaskId(taskIds[0] || null);
      setEditNotice(null);
      setEditError(null);

      try {
        for (const taskId of taskIds) {
          if (cancelled) return;
          await azureSqlService.updateTask(taskId, { status: 'Completed' });
        }
        if (cancelled) return;

        setShowCompletedTasks(true);
        setStatusFilter('completed');
        setSearchQuery('');
        setTaskStatusUndoNotice(null);
        setEditNotice(`${taskIds.length} task${taskIds.length === 1 ? '' : 's'} marked Completed from email.`);
        playSuccess();
        await loadTasks({
          showSpinner: false,
          playAudio: false,
          query: '',
          useCache: false,
        }).catch(() => undefined);
      } catch (err) {
        if (!cancelled) {
          setEditError(formatUserFacingError(err, 'One or more tasks could not be completed from the email update.'));
          playError();
        }
      } finally {
        if (!cancelled) {
          setQuickUpdateTaskId(null);
          params.delete('completeTaskId');
          params.delete('completeTaskNumber');
          params.delete('completeTaskIds');
          params.delete('completeTaskSource');
          navigate(
            {
              pathname: location.pathname,
              search: params.toString() ? `?${params.toString()}` : '',
            },
            { replace: true, state: location.state }
          );
        }
      }
    };

    void completeTaskFromEmail();

    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, user, location.pathname, location.search, location.state, navigate, playError, playSuccess]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, dateFrom, dateTo, activeDateBasis, statusFilter, showCompletedTasks, oldestFirst]);

  useEffect(() => {
    const wasSearchActive = previousSearchActiveRef.current;
    if (isSearchActive && !wasSearchActive) {
      setFormOpen(false);
      setReminderOpen(false);
    }
    previousSearchActiveRef.current = isSearchActive;
  }, [isSearchActive]);

  useEffect(() => {
    if (!aiFocus && !aiPeriodFilter) {
      return;
    }

    setStatusFilter('all');
    setShowCompletedTasks(true);
    if (aiPeriodFilter) {
      setSearchQuery('');
      setDateFrom(normalizeDateOnlyInput(aiPeriodFilter.startDate));
      setDateTo(normalizeDateOnlyInput(aiPeriodFilter.endDate));
      return;
    }

    if (!aiFocus.recordId) {
      setSearchQuery(aiFocus.searchText);
      return;
    }

    setSearchQuery((current) => current.trim() || aiFocus.searchText);
  }, [aiFocus?.recordId, aiFocus?.searchText, aiPeriodFilter?.startDate, aiPeriodFilter?.endDate, aiPeriodFilter?.dateBasis]);

  useEffect(() => {
    if (!formOpen) {
      return;
    }

    const query = projectSearchQuery.trim();
    const selectedProjectQuery = selectedProject ? buildProjectSearchValue(selectedProject) : '';
    if (query.length < 2) {
      setProjectOptions([]);
      setProjectSearchLoading(false);
      setProjectSearchError(null);
      return;
    }
    if (selectedProject && query === selectedProjectQuery) {
      setProjectOptions([]);
      setProjectSearchLoading(false);
      setProjectSearchError(null);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setProjectSearchLoading(true);
      setProjectSearchError(null);
      try {
        const projectData = await azureSqlService.searchProjects(query, 8, { lifecycle: 'linkable' });
        if (cancelled) return;
        const mappedProjects = normalizeProjectSearchResults(projectData);
        setProjectOptions(mappedProjects);
      } catch (err) {
        if (cancelled) return;
        setProjectSearchError(formatUserFacingError(err, 'Project search could not load.'));
        setProjectOptions([]);
      } finally {
        if (!cancelled) {
          setProjectSearchLoading(false);
        }
      }
    }, 150);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [projectSearchQuery, formOpen, selectedProject]);

  useEffect(() => {
    if (!formOpen) {
      return;
    }

    const descriptionQuery = form.description.trim();
    const manualProjectQuery = projectSearchQuery.trim();
    if (selectedProject || manualProjectQuery.length >= 2 || descriptionQuery.length < 4) {
      setDescriptionMatchOptions([]);
      setDescriptionMatchLoading(false);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setDescriptionMatchLoading(true);
      try {
        const projectData = await azureSqlService.matchProjectsFromDescription(descriptionQuery, 5, { lifecycle: 'linkable' });
        if (cancelled) return;
        const mappedProjects = normalizeProjectSearchResults(projectData);
        setDescriptionMatchOptions(mappedProjects);
      } catch (err) {
        if (cancelled) return;
        setDescriptionMatchOptions([]);
      } finally {
        if (!cancelled) {
          setDescriptionMatchLoading(false);
        }
      }
    }, 220);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [form.description, formOpen, projectSearchQuery, selectedProject]);

  useEffect(() => {
    if (!editingTaskId || !editForm) {
      return;
    }

    const query = editProjectSearchQuery.trim();
    const selectedProjectQuery = editSelectedProject ? buildProjectSearchValue(editSelectedProject) : '';
    if (query.length < 2) {
      setEditProjectOptions([]);
      setEditProjectSearchLoading(false);
      setEditProjectSearchError(null);
      return;
    }
    if (editSelectedProject && query === selectedProjectQuery) {
      setEditProjectOptions([]);
      setEditProjectSearchLoading(false);
      setEditProjectSearchError(null);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setEditProjectSearchLoading(true);
      setEditProjectSearchError(null);
      try {
        const projectData = await azureSqlService.searchProjects(query, 8, { lifecycle: 'linkable' });
        if (cancelled) return;
        const mappedProjects = normalizeProjectSearchResults(projectData);
        setEditProjectOptions(mappedProjects);
      } catch (err) {
        if (cancelled) return;
        setEditProjectSearchError(formatUserFacingError(err, 'Project search could not load.'));
        setEditProjectOptions([]);
      } finally {
        if (!cancelled) {
          setEditProjectSearchLoading(false);
        }
      }
    }, 150);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [editProjectSearchQuery, editSelectedProject, editingTaskId, editForm]);

  const loadTasks = async ({
    showSpinner = true,
    playAudio = true,
    query = searchQuery.trim(),
    useCache = !query && !hasActiveDateRange && page === 1,
  }: {
    showSpinner?: boolean;
    playAudio?: boolean;
    query?: string;
    useCache?: boolean;
  } = {}) => {
    if (showSpinner) {
      setLoading(true);
      setError(null);
    }

    try {
        const taskResult = await azureSqlService.getTasks({
          page: isTaskDetailMode ? 1 : page,
          pageSize: isTaskDetailMode ? 1 : pageSize,
          sort: sortBy === 'urgency' ? 'urgency' : undefined,
          query: {
            ...(isTaskDetailMode
              ? { taskId: taskDetailId }
              : {
                  ...(query ? { q: query } : {}),
                  ...(dateFrom ? { dateFrom } : {}),
                  ...(dateTo ? { dateTo } : {}),
                  ...(hasActiveDateRange ? { dateBasis: activeDateBasis } : {}),
                  oldestFirst: oldestFirst ? '1' : '0',
                }),
          },
          useCache: isTaskDetailMode ? false : useCache,
          returnEnvelope: true,
        });
      const taskData = taskResult?.data || [];

      const mappedTasks = (taskData || []).map((task: any) => ({
        id: task.TaskId?.toString() || task.TaskNumber,
        taskNumber: task.TaskNumber || `T${task.TaskId}`,
        title: stripSystemTaskPrefix(task.Title || 'Untitled task'),
        description: stripSystemTaskPrefix(task.Description || task.Title || 'Untitled task'),
        category: task.Category || 'To Do',
        taskType: task.TaskType || 'Standard',
        projectId: typeof task.ProjectId === 'number' ? task.ProjectId : task.ProjectId ? Number(task.ProjectId) : null,
        projectNumber: task.ProjectNumber || '',
        projectName: task.ProjectName || '',
        projectSuburb: formatSuburbLabel(task.ProjectSuburb || ''),
        projectCouncil: task.ProjectCouncil || '',
        projectLocation: task.ProjectLocation || '',
        assignedTo: task.AssignedTo || 'Unassigned',
        assignedToEmail: task.AssignedToEmail || '',
        createdBy: task.CreatedBy || '',
        priority: task.Priority || 'Medium',
        pricingAmount: task.PricingAmount != null && task.PricingAmount !== '' ? Number(task.PricingAmount) : null,
        pricingCurrency: task.PricingCurrency || 'AUD',
        rfiTicketNumber: task.RfiTicketNumber || '',
        responseEmail: task.ResponseEmail || '',
        linkedInvoiceId: typeof task.LinkedInvoiceId === 'number' ? task.LinkedInvoiceId : task.LinkedInvoiceId ? Number(task.LinkedInvoiceId) : null,
        linkedInvoiceNumber: task.LinkedInvoiceNumber || '',
        linkedInvoiceSource: task.LinkedInvoiceSource || '',
        invoiceStatus: task.InvoiceStatus || '',
        invoicePaidDate: task.InvoicePaidDate || '',
        paymentStatus: task.PaymentStatus || '',
        stripePaymentUrl: task.StripePaymentUrl || '',
        stripePaymentReference: task.StripePaymentReference || '',
        priorityCode: task.PriorityCode || '2',
        status: task.Status || 'Pending',
        statusCode: task.StatusCode || '1',
        createdDate: task.CreatedDate || task.CreatedAt || task.Created || '',
        startDate: task.StartDate || '',
        dueDate: task.DueDate || '',
        completedDate: task.CompletedDate || '',
        modifiedDate: task.ModifiedDate || '',
        remainingDays: typeof task.RemainingDays === 'number'
          ? task.RemainingDays
          : task.RemainingDays !== null && task.RemainingDays !== undefined
            ? Number(task.RemainingDays)
            : null,
        urgencyScore: task.UrgencyScore === null || task.UrgencyScore === undefined
          ? null
          : Number(task.UrgencyScore),
      }));

      setTasks(mappedTasks);
      setPagination({
        page: Number(taskResult?.pagination?.page || page),
        pageSize: Number(taskResult?.pagination?.pageSize || pageSize),
        totalRecords: Number(taskResult?.pagination?.totalRecords || mappedTasks.length || 0),
        totalPages: Math.max(Number(taskResult?.pagination?.totalPages || 1), 1),
      });
      if (playAudio) {
        playSuccess();
      }
      return mappedTasks;
    } catch (err) {
      if (showSpinner) {
        setError(formatUserFacingError(
          err,
          'Task data could not load.',
          'Use Retry to load the list again. If this keeps happening, check the API connection.'
        ));
      }
      if (playAudio) {
        playError();
      }
      throw err;
    } finally {
      if (showSpinner) {
        setLoading(false);
      }
    }
  };

  const toggleReminderPanel = () => {
    setReminderOpen((current) => !current);
    setReminderError(null);
    setReminderNotice(null);
  };

  const handleSendReminder = async () => {
    const selectedRecipient = reminderRecipientOptions.find((option) => option.value === reminderRecipientEmail);
    if (!selectedRecipient?.value) {
      setReminderError('Pick a person to remind first.');
      return;
    }

    setReminderSending(true);
    setReminderError(null);
    setReminderNotice(null);

    try {
      const result = await azureSqlService.sendTaskReminder({
        recipientEmail: selectedRecipient.value,
        recipientName: selectedRecipient.recipientName,
        taskListUrl: window.location.origin,
      });

      const openTaskCount = typeof result?.openTaskCount === 'number' ? result.openTaskCount : null;
      setReminderNotice(
        openTaskCount === null
          ? `Reminder sent to ${selectedRecipient.label}.`
          : `Reminder sent to ${selectedRecipient.label} with ${openTaskCount} open task${openTaskCount === 1 ? '' : 's'}.`
      );
      playSuccess();
    } catch (err) {
      setReminderError(formatUserFacingError(err, 'Task reminder could not be sent.'));
      playError();
    } finally {
      setReminderSending(false);
    }
  };

  const resetForm = () => {
    setForm(createInitialForm(currentUserEmail));
    setSelectedProject(null);
    setProjectSearchQuery('');
    setProjectOptions([]);
    setDescriptionMatchOptions([]);
    setDescriptionMatchLoading(false);
    setProjectSearchError(null);
    setFormError(null);
    setFormNotice(null);
    setAssigneeDirty(false);
  };

  const resetEditState = () => {
    setEditingTaskId(null);
    setEditForm(null);
    setEditSaving(false);
    setEditError(null);
    setEditProjectSearchQuery('');
    setEditProjectSearchLoading(false);
    setEditProjectSearchError(null);
    setEditProjectOptions([]);
    setEditSelectedProject(null);
  };

  const startEditingTask = (task: TaskRecord) => {
    if (editingTaskId === task.id) {
      resetEditState();
      return;
    }

    setEditNotice(null);
    setEditError(null);
    setEditingTaskId(task.id);
    setEditForm(createInitialEditForm(task, currentUserEmail));
    const editableProject = buildEditableProjectFromTask(task);
    setEditSelectedProject(editableProject);
    setEditProjectSearchQuery(editableProject ? buildProjectSearchValue(editableProject) : '');
    setEditProjectOptions([]);
    setEditProjectSearchError(null);
  };

  const handleCategoryChange = (category: TaskCategory) => {
    setForm((current) => ({
      ...current,
      category,
      assignedToEmail: assigneeDirty ? current.assignedToEmail : defaultAssigneeForCategory(category, currentUserEmail),
    }));
  };

  const handleTaskTypeChange = (taskType: TaskType) => {
    setForm((current) => {
      const nextDueDate = shouldAutoUpdateTaskDueDate(current.dueDate, current.startDate, current.taskType)
        ? getDefaultTaskDueDate(current.startDate, taskType)
        : current.dueDate;
      return {
        ...current,
        taskType,
        dueDate: nextDueDate,
      };
    });
  };

  const handleStartDateChange = (startDate: string) => {
    setForm((current) => {
      const nextDueDate = shouldAutoUpdateTaskDueDate(current.dueDate, current.startDate, current.taskType)
        ? getDefaultTaskDueDate(startDate, current.taskType)
        : current.dueDate;
      return {
        ...current,
        startDate,
        dueDate: nextDueDate,
      };
    });
  };

  const handleEditCategoryChange = (category: TaskCategory) => {
    setEditForm((current) => {
      if (!current) return current;
      const currentDefault = defaultAssigneeForCategory(current.category, currentUserEmail);
      const nextDefault = defaultAssigneeForCategory(category, currentUserEmail);
      return {
        ...current,
        category,
        assignedToEmail: current.assignedToEmail === currentDefault ? nextDefault : current.assignedToEmail,
      };
    });
  };

  const handleEditTaskTypeChange = (taskType: TaskType) => {
    setEditForm((current) => {
      if (!current) return current;
      const nextDueDate = shouldAutoUpdateTaskDueDate(current.dueDate, current.startDate, current.taskType)
        ? getDefaultTaskDueDate(current.startDate, taskType)
        : current.dueDate;
      return {
        ...current,
        taskType,
        dueDate: nextDueDate,
      };
    });
  };

  const handleEditStartDateChange = (startDate: string) => {
    setEditForm((current) => {
      if (!current) return current;
      const nextDueDate = shouldAutoUpdateTaskDueDate(current.dueDate, current.startDate, current.taskType)
        ? getDefaultTaskDueDate(startDate, current.taskType)
        : current.dueDate;
      return {
        ...current,
        startDate,
        dueDate: nextDueDate,
      };
    });
  };

  const handleCreateTask = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    setFormNotice(null);

    const description = form.description.trim();
    if (!description) {
      setFormError('Description is required.');
      return;
    }
    if (form.taskType === 'RFI' && (!Number(form.pricingAmount) || Number(form.pricingAmount) <= 0)) {
      setFormError('RFI tasks need a pricing amount.');
      return;
    }
    if (!isValidOptionalEmail(form.responseEmail)) {
      setFormError('Enter a valid responding email or leave it blank.');
      return;
    }

    if (form.dueDate && form.startDate && form.dueDate < form.startDate) {
      setFormError('Due date cannot be earlier than the start date.');
      return;
    }

    setSaving(true);
    try {
      const result = await azureSqlService.createTask({
        category: form.category,
        taskType: form.taskType,
        priority: form.priority,
        description,
        startDate: form.startDate,
        dueDate: form.dueDate || null,
        projectId: selectedProject?.projectId ?? null,
        assignedToEmail: form.assignedToEmail || null,
        pricingAmount: form.pricingAmount ? Number(form.pricingAmount) : null,
        pricingCurrency: form.pricingCurrency || 'AUD',
        rfiTicketNumber: form.rfiTicketNumber || null,
        responseEmail: form.responseEmail.trim() || null,
      });

      playSuccess();
      const successNotice = Array.isArray(result?.warnings) && result.warnings.length > 0
        ? result.warnings.join(' ')
        : 'Task created successfully.';
      resetForm();
      setFormNotice(successNotice);
      await loadTasks({ showSpinner: false, playAudio: false, useCache: false }).catch(() => undefined);
    } catch (err) {
      setFormError(formatUserFacingError(err, 'Task could not be created.'));
      playError();
    } finally {
      setSaving(false);
    }
  };

  const handleQuickTaskUpdate = async (
    task: TaskRecord,
    payload: Record<string, unknown>,
    nextEditValues: Partial<TaskEditState>,
    successMessage: string,
    options?: {
      onSuccess?: () => void;
      clearUndoNotice?: boolean;
    }
  ) => {
    setQuickUpdateTaskId(task.id);
    setEditNotice(null);
    setEditError(null);

    try {
      await azureSqlService.updateTask(task.id, payload);
      if (editingTaskId === task.id) {
        setEditForm((current) => current ? { ...current, ...nextEditValues } : current);
      }
      setEditNotice(successMessage);
      if (options?.clearUndoNotice !== false) {
        setTaskStatusUndoNotice(null);
      }
      options?.onSuccess?.();
      playSuccess();
      await loadTasks({ showSpinner: false, playAudio: false, useCache: false }).catch(() => undefined);
    } catch (err) {
      setEditError(formatUserFacingError(err, 'Task could not be updated.'));
      playError();
    } finally {
      setQuickUpdateTaskId(null);
    }
  };

  const handleQuickCategoryChange = async (task: TaskRecord, nextCategory: TaskCategory) => {
    if (normalizeTaskCategory(task.category) === nextCategory) {
      return;
    }

    await handleQuickTaskUpdate(
      task,
      { category: nextCategory },
      { category: nextCategory },
      `${task.taskNumber} updated to ${nextCategory}.`
    );
  };

  const handleQuickPriorityChange = async (task: TaskRecord, nextPriority: TaskPriority) => {
    if (normalizeTaskPriority(task.priority) === nextPriority) {
      return;
    }

    await handleQuickTaskUpdate(
      task,
      { priority: nextPriority },
      { priority: nextPriority },
      `${task.taskNumber} updated to ${nextPriority} priority.`
    );
  };

  const handleQuickStatusChange = async (task: TaskRecord, nextStatus: TaskStatus) => {
    const previousStatus = normalizeTaskStatus(task.status);
    if (previousStatus === nextStatus) {
      return;
    }

    await handleQuickTaskUpdate(
      task,
      { status: nextStatus },
      { status: nextStatus },
      `${task.taskNumber} updated to ${nextStatus}.`,
      {
        onSuccess: () => {
          if (nextStatus === 'Completed') {
            setTaskStatusUndoNotice({
              taskId: task.id,
              taskNumber: task.taskNumber,
              previousStatus,
            });
          }
        },
      }
    );
  };

  const handleUndoCompletedStatusChange = async () => {
    if (!taskStatusUndoNotice) {
      return;
    }

    setQuickUpdateTaskId(taskStatusUndoNotice.taskId);
    setEditError(null);

    try {
      await azureSqlService.updateTask(taskStatusUndoNotice.taskId, {
        status: taskStatusUndoNotice.previousStatus,
      });
      if (editingTaskId === taskStatusUndoNotice.taskId) {
        setEditForm((current) => current ? { ...current, status: taskStatusUndoNotice.previousStatus } : current);
      }
      setEditNotice(`${taskStatusUndoNotice.taskNumber} reverted to ${taskStatusUndoNotice.previousStatus}.`);
      setTaskStatusUndoNotice(null);
      playSuccess();
      await loadTasks({ showSpinner: false, playAudio: false, useCache: false }).catch(() => undefined);
    } catch (err) {
      setEditError(formatUserFacingError(err, 'Task status could not be restored.'));
      playError();
    } finally {
      setQuickUpdateTaskId(null);
    }
  };

  const handleSaveTaskEdit = async (task: TaskRecord) => {
    if (!editForm) {
      return;
    }

    const title = editForm.title.trim();
    const taskNumber = editForm.taskNumber.trim();
    const description = editForm.description.trim();
    if (!title) {
      setEditError('Task name is required.');
      return;
    }
    if (!description) {
      setEditError('Description is required.');
      return;
    }
    if (editForm.taskType === 'RFI' && (!Number(editForm.pricingAmount) || Number(editForm.pricingAmount) <= 0)) {
      setEditError('RFI tasks need a pricing amount.');
      return;
    }
    if (!isValidOptionalEmail(editForm.responseEmail)) {
      setEditError('Enter a valid responding email or leave it blank.');
      return;
    }

    if (editForm.dueDate && editForm.startDate && editForm.dueDate < editForm.startDate) {
      setEditError('Due date cannot be earlier than the start date.');
      return;
    }

    setEditSaving(true);
    setEditError(null);
    setEditNotice(null);
    setTaskStatusUndoNotice(null);

    try {
      await azureSqlService.updateTask(task.id, {
        title,
        taskNumber: taskNumber || task.taskNumber,
        description,
        category: editForm.category,
        taskType: editForm.taskType,
        priority: editForm.priority,
        status: editForm.status,
        startDate: editForm.startDate,
        dueDate: editForm.dueDate || null,
        assignedToEmail: editForm.assignedToEmail || null,
        projectId: editSelectedProject?.projectId ?? null,
        pricingAmount: editForm.pricingAmount ? Number(editForm.pricingAmount) : null,
        pricingCurrency: editForm.pricingCurrency || 'AUD',
        rfiTicketNumber: editForm.rfiTicketNumber || null,
        responseEmail: editForm.responseEmail.trim() || null,
      });

      setEditNotice(`${task.taskNumber} saved.`);
      playSuccess();
      await loadTasks({ showSpinner: false, playAudio: false, useCache: false }).catch(() => undefined);
      resetEditState();
    } catch (err) {
      setEditError(formatUserFacingError(err, 'Task could not be saved.'));
      playError();
    } finally {
      setEditSaving(false);
    }
  };

  const openLinkedInvoice = (task: TaskRecord) => {
    if (!task.linkedInvoiceId && !task.linkedInvoiceNumber) {
      setEditError('This task is not linked to an invoice yet.');
      return;
    }

    navigate('/invoices/workflow', {
      state: {
        mode: 'amend',
        invoiceId: task.linkedInvoiceId ? String(task.linkedInvoiceId) : undefined,
        invoiceNumber: task.linkedInvoiceNumber || undefined,
      },
    });
  };

  const handleGenerateInvoice = async (task: TaskRecord) => {
    setEditNotice(null);
    setEditError(null);

    if (normalizeTaskType(task.taskType) !== 'RFI') {
      setEditError('Only RFI tasks can generate invoice drafts from the task card.');
      return;
    }
    if (!task.projectId || !task.projectNumber) {
      setEditError('Link the RFI task to a project before generating its invoice.');
      return;
    }
    if (!task.rfiTicketNumber) {
      setEditError('This RFI task is missing its ticket number.');
      return;
    }
    if (!task.pricingAmount || task.pricingAmount <= 0) {
      setEditError('Add a pricing amount before generating the RFI invoice.');
      return;
    }

    setGeneratingInvoiceTaskId(task.id);
    try {
      const response = await azureSqlService.createWorkflowInvoice({
        taskId: task.id,
        projectId: task.projectId,
        projectNumber: task.projectNumber,
        projectAddress: task.projectLocation || undefined,
        rfiTicketNumber: task.rfiTicketNumber,
        pricingCurrency: task.pricingCurrency || 'AUD',
        stripePaymentMode: 'none',
        notes: `RFI ${task.rfiTicketNumber}`,
        createSendTask: false,
        items: [
          {
            categoryName: 'RFI',
            description: `RFI ${task.rfiTicketNumber}`,
            quantity: 1,
            unitPrice: task.pricingAmount,
            taxRate: 0.1,
          },
        ],
      });

      const invoiceId = String(response?.invoiceId || response?.data?.invoiceId || '').trim();
      const invoiceNumber = String(response?.invoiceNumber || response?.data?.invoiceNumber || '').trim();
      await loadTasks({ showSpinner: false, playAudio: false, useCache: false }).catch(() => undefined);
      playSuccess();
      setEditNotice(`Created invoice ${invoiceNumber || invoiceId} for ${task.taskNumber}.`);
      navigate('/invoices/workflow', {
        state: {
          mode: 'amend',
          invoiceId: invoiceId || undefined,
          invoiceNumber: invoiceNumber || undefined,
        },
      });
    } catch (err) {
      setEditError(formatUserFacingError(err, 'Invoice could not be generated from this task.'));
      playError();
    } finally {
      setGeneratingInvoiceTaskId(null);
    }
  };

  const handleDeleteTask = async (task: TaskRecord) => {
    if (!window.confirm(`Soft delete ${task.taskNumber}? This hides it from the active task list.`)) {
      return;
    }

    setDeletingTaskId(task.id);
    setEditNotice(null);
    setEditError(null);

    try {
      await azureSqlService.deleteTask(task.id);
      if (editingTaskId === task.id) {
        resetEditState();
      }
      setEditNotice(`${task.taskNumber} deleted.`);
      playSuccess();
      await loadTasks({ showSpinner: false, playAudio: false, useCache: false }).catch(() => undefined);
    } catch (err) {
      setEditError(formatUserFacingError(err, 'Task could not be deleted.'));
      playError();
    } finally {
      setDeletingTaskId(null);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'No date';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getStatusTone = (status: string) => {
    const normalized = String(status || '').trim().toLowerCase();
    if (normalized === 'completed' || normalized === 'complete' || normalized === 'done') {
      return { color: '#0F8A63', background: '#E8F7F1' };
    }
    if (normalized === 'in progress' || normalized === 'progress' || normalized === 'ongoing') {
      return { color: '#2251B4', background: '#EAF1FF' };
    }
    if (normalized === 'overdue') {
      return { color: '#C93535', background: '#FDECEC' };
    }
    return { color: '#5B6472', background: '#F5F8FC' };
  };

  const getPriorityTone = (priority: string) => {
    const normalized = String(priority || '').trim().toLowerCase();
    if (normalized === 'critical') {
      return { color: '#972A2A', background: '#FDECEC' };
    }
    if (normalized === 'high') {
      return { color: '#C93535', background: '#FDECEC' };
    }
    if (normalized === 'low') {
      return { color: '#0F8A63', background: '#E8F7F1' };
    }
    return { color: '#C06C16', background: '#FFF4E7' };
  };

  const getCategoryColor = (category: string) => {
    const normalized = String(category || '').trim().toLowerCase();
    if (normalized === 'admin') return { color: '#6b21a8', background: '#f3e8ff' };
    if (normalized === 'engineering') return { color: '#0f766e', background: '#ccfbf1' };
    if (normalized === 'finance') return { color: '#9a3412', background: '#ffedd5' };
    if (normalized === 'civil') return { color: '#2251B4', background: '#EAF1FF' };
    if (normalized === 'structural') return { color: '#173B84', background: '#DDE8FF' };
    return { color: '#475467', background: '#F5F8FC' };
  };

  const formatRemainingDays = (remainingDays: number | null, dueDate?: string) => {
    if (remainingDays === null || remainingDays === undefined || !dueDate) return 'No due date';
    if (remainingDays < 0) return `${Math.abs(remainingDays)}d overdue`;
    if (remainingDays === 0) return 'Due today';
    return `${remainingDays}d left`;
  };

  const getTaskTimelineDisplay = (task: TaskRecord) => {
    const normalizedStatus = normalizeTaskStatus(task.status);
    if (normalizedStatus === 'Completed') {
      return {
        headline: 'Completed',
        dateLabel: task.completedDate ? formatDate(task.completedDate) : 'Completion date not set',
        color: '#0F8A63',
      };
    }

    return {
      headline: formatRemainingDays(task.remainingDays, task.dueDate),
      dateLabel: formatDate(task.dueDate),
      color: task.remainingDays !== null && task.remainingDays < 0 ? '#C93535' : '#5B6472',
    };
  };

  const filteredTasks = isTaskDetailMode
    ? tasks.filter((task) => String(task.id) === taskDetailId)
    : filterVisibleTasks(tasks, {
        showCompletedTasks,
        statusFilter,
        searchQuery,
      });

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    if (oldestFirst && sortBy !== 'urgency') {
      const oldestComparison = compareOldestTasksFirst(a, b);
      if (oldestComparison !== 0) {
        return oldestComparison;
      }
    }

    const aAssignedToMe = normalizeComparableText(a.assignedToEmail) === currentUserEmail;
    const bAssignedToMe = normalizeComparableText(b.assignedToEmail) === currentUserEmail;
    const aCreatedByMe = normalizeComparableText(a.createdBy) === currentUserEmail;
    const bCreatedByMe = normalizeComparableText(b.createdBy) === currentUserEmail;

    let comparison = 0;
    switch (sortBy) {
      case 'urgency':
        comparison = calculateTaskUrgencyScore(a) - calculateTaskUrgencyScore(b);
        break;
      case 'due': {
        const da = a.dueDate ? new Date(a.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
        const db = b.dueDate ? new Date(b.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
        comparison = da - db;
        break;
      }
      case 'remaining': {
        const ra = a.remainingDays ?? Number.MAX_SAFE_INTEGER;
        const rb = b.remainingDays ?? Number.MAX_SAFE_INTEGER;
        comparison = ra - rb;
        break;
      }
      case 'priority':
        comparison = String(a.priorityCode || '').localeCompare(String(b.priorityCode || ''));
        break;
      case 'status':
        comparison = String(a.statusCode || '').localeCompare(String(b.statusCode || ''));
        break;
      case 'workflow':
        comparison = getTaskWorkflowSortRank(a) - getTaskWorkflowSortRank(b);
        break;
      case 'assigned':
        comparison = aAssignedToMe === bAssignedToMe
          ? compareTaskNumbers(a.taskNumber, b.taskNumber)
          : Number(aAssignedToMe) - Number(bAssignedToMe);
        break;
      case 'created':
        comparison = aCreatedByMe === bCreatedByMe
          ? compareTaskNumbers(a.taskNumber, b.taskNumber)
          : Number(aCreatedByMe) - Number(bCreatedByMe);
        break;
      default:
        comparison = compareTaskNumbers(a.taskNumber, b.taskNumber);
        break;
    }

    return sortDirection === 'desc' ? comparison * -1 : comparison;
  });
  const visibleTasks = sortedTasks;

  useEffect(() => {
    if (!aiFocus || loading) {
      return;
    }

    const focusId = getCompanyAiFocusDomId('tasks', aiFocus.recordId);
    if (!focusId || !visibleTasks.some((task) => matchesCompanyAiFocusRecord(aiFocus, task.id))) {
      return;
    }

    const timer = window.setTimeout(() => {
      const element = document.getElementById(focusId);
      if (!element) {
        return;
      }

      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (typeof (element as HTMLElement).focus === 'function') {
        (element as HTMLElement).focus({ preventScroll: true });
      }
    }, 120);

    return () => window.clearTimeout(timer);
  }, [aiFocus, loading, visibleTasks]);

  const handleSortChange = (value: string) => {
    const nextSortBy = value as TaskSortBy;
    setSortBy(nextSortBy);
    setSortDirection(getDefaultSortDirection(nextSortBy));
    setPage(1);
  };

  const toggleSortDirection = () => {
    setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
  };

  const handleCompletedVisibilityChange = (nextValue: boolean) => {
    setShowCompletedTasks(nextValue);
    if (!nextValue && statusFilter === 'completed') {
      setStatusFilter('all');
    }
  };

  const handleOldestFirstChange = (nextValue: boolean) => {
    forceTaskRefreshRef.current = true;
    setOldestFirst(nextValue);
    if (!nextValue) {
      setSortBy('number');
      setSortDirection('desc');
    }
    writeTaskOldestFirstPreference(nextValue);
    setPage(1);
  };

  const statusFilterOptions: Array<{ value: TaskStatusFilter; label: string }> = [
    { value: 'all', label: 'All tasks' },
    { value: 'todo', label: 'To do' },
    { value: 'inprogress', label: 'In progress' },
    { value: 'engineering', label: 'Engineering' },
    { value: 'finance', label: 'Finance' },
    { value: 'rfi_to_invoice', label: 'RFI to invoice' },
    { value: 'rfi_pending_payment', label: 'RFI pending payment' },
    { value: 'rfi_paid', label: 'RFI paid' },
    ...(showCompletedTasks ? [{ value: 'completed' as const, label: 'Completed' }] : []),
    { value: 'overdue', label: 'Overdue' },
  ];

  const compactProjectInputStyle: React.CSSProperties = {
    boxSizing: 'border-box',
  };

  const taskQuickChipSelectBaseStyle: React.CSSProperties = {
    minWidth: 0,
    maxWidth: '100%',
    minHeight: '22px',
    padding: '3px 8px',
    borderRadius: '999px',
    fontFamily: 'var(--font-family)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-semibold)',
    letterSpacing: '0.05em',
    lineHeight: 1.1,
    boxSizing: 'border-box',
    textAlign: 'center',
    textAlignLast: 'center',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    backgroundImage: 'none',
    cursor: 'pointer',
  };

  const secondaryButtonStyle: React.CSSProperties = {
    border: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-surface)',
    color: 'var(--color-text)',
    minHeight: isNarrowViewport ? '40px' : '34px',
    padding: isNarrowViewport ? '8px 12px' : '6px 14px',
    borderRadius: '999px',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: isNarrowViewport ? '12px' : '11px',
    lineHeight: 1.2,
  };

  const primaryButtonStyle: React.CSSProperties = {
    border: '1px solid var(--color-primary)',
    background: 'linear-gradient(135deg, #2251B4 0%, #173B84 100%)',
    color: '#fff',
    minHeight: isNarrowViewport ? '40px' : '34px',
    padding: isNarrowViewport ? '9px 16px' : '6px 14px',
    borderRadius: '999px',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: isNarrowViewport ? '12px' : '11px',
    lineHeight: 1.2,
  };

  const subtleInfoButtonStyle: React.CSSProperties = {
    ...secondaryButtonStyle,
    border: '1px solid rgba(34, 81, 180, 0.18)',
    backgroundColor: 'var(--color-primary-soft)',
    color: 'var(--color-primary)',
  };

  const warningButtonStyle: React.CSSProperties = {
    ...secondaryButtonStyle,
    border: '1px solid rgba(192, 108, 22, 0.2)',
    backgroundColor: 'var(--color-warning-soft)',
    color: '#9A5713',
  };

  const dangerButtonStyle: React.CSSProperties = {
    ...secondaryButtonStyle,
    border: '1px solid rgba(201, 53, 53, 0.18)',
    backgroundColor: '#FEF1F1',
    color: '#972A2A',
  };

  const detailTask = isTaskDetailMode && !loading && !error ? visibleTasks[0] : undefined;
  const detailTaskBusy = Boolean(detailTask && (
    quickUpdateTaskId === detailTask.id || editSaving || generatingInvoiceTaskId === detailTask.id || deletingTaskId === detailTask.id
  ));
  const detailTaskEditing = Boolean(detailTask && editingTaskId === detailTask.id && editForm);
  const detailTaskStatus = detailTaskEditing && editForm ? editForm.status : normalizeTaskStatus(detailTask?.status || '');
  const detailTaskType = detailTaskEditing && editForm ? editForm.taskType : normalizeTaskType(detailTask?.taskType || '');

  return (
    <div className={`dgf-page task-list-page${isMobileCreateMode ? ' task-list-page--mobile-create' : ''}${isTaskDetailMode ? ' task-list-page--detail' : ''}`}>
      {!isMobileCreateMode && !isTaskDetailMode && <AssistantInbox onTasksChanged={() => { void loadTasks({ showSpinner: false, playAudio: false, useCache: false }); }} />}
      {isTaskDetailMode ? (
        <div className="dgf-detail-header">
          <div className="dgf-detail-heading">
            <button type="button" className="dgf-detail-back" aria-label="Back to task list" onClick={() => navigate('/tasks')}>
              <ChevronLeft size={22} aria-hidden="true" />
            </button>
            <h1>Task {detailTask?.taskNumber || taskDetailId}</h1>
          </div>
          {detailTask ? (
            <div className="dgf-detail-actions dgf-detail-actions--primary">
              <Button size="sm" variant="secondary" onClick={() => startEditingTask(detailTask)} disabled={detailTaskBusy}
                aria-label={detailTaskEditing ? `Close editor for ${detailTask.taskNumber}` : `Edit ${detailTask.taskNumber}`}
                iconBefore={detailTaskEditing ? <X size={18} aria-hidden="true" /> : <Pencil size={18} aria-hidden="true" />}>
                {detailTaskEditing ? 'Close' : 'Edit'}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => void handleQuickStatusChange(detailTask, 'Completed')}
                disabled={detailTaskStatus === 'Completed' || quickUpdateTaskId === detailTask.id || editSaving || deletingTaskId === detailTask.id}
                aria-label={detailTaskStatus === 'Completed' ? `${detailTask.taskNumber} is completed` : `Mark ${detailTask.taskNumber} completed`}
                iconBefore={<Check size={18} aria-hidden="true" />}>
                {quickUpdateTaskId === detailTask.id ? 'Updating...' : detailTaskStatus === 'Completed' ? 'Completed' : 'Complete'}
              </Button>
              {detailTask.linkedInvoiceId || detailTask.linkedInvoiceNumber ? (
                <Button size="sm" variant="secondary" onClick={() => openLinkedInvoice(detailTask)} disabled={detailTaskBusy}
                  aria-label="Open linked invoice" iconBefore={<FileText size={18} aria-hidden="true" />}>
                  Invoice
                </Button>
              ) : null}
              <DetailActionMenu label="More task actions" items={[
                ...(detailTaskType === 'RFI' && !detailTask.linkedInvoiceId && !detailTask.linkedInvoiceNumber ? [{
                  label: generatingInvoiceTaskId === detailTask.id ? 'Generating...' : 'Generate Invoice',
                  icon: <FilePlus2 size={18} aria-hidden="true" />,
                  onClick: () => void handleGenerateInvoice(detailTask),
                  disabled: detailTaskBusy,
                }] : []),
                ...(String(detailTask.assignedToEmail || '').trim().toLowerCase() === WILSON_EMAIL ? [{
                  label: 'Send Email',
                  icon: <Mail size={18} aria-hidden="true" />,
                  onClick: () => setEmailTarget({ entityType: 'task', entityId: Number(detailTask.id), label: detailTask.taskNumber || `Task ${detailTask.id}` }),
                  disabled: detailTaskBusy,
                  title: 'Send task email to Wilson',
                }] : []),
                {
                  label: deletingTaskId === detailTask.id ? 'Deleting...' : 'Delete Task',
                  icon: <Trash2 size={18} aria-hidden="true" />,
                  onClick: () => void handleDeleteTask(detailTask),
                  disabled: detailTaskBusy,
                  danger: true,
                },
              ]} />
            </div>
          ) : null}
        </div>
      ) : <PageHeader
        title={isTaskDetailMode ? 'Task detail' : 'Task list'}
        subtitle={isTaskDetailMode
          ? 'Review and update this task without searching through the full list.'
          : 'Add tasks, link them to projects, and track due dates in one place.'}
        itemCount={visibleTasks.length}
        gradientColor="linear-gradient(135deg, #173b84 0%, #2251b4 54%, #4b6fbc 100%)"
        icon="✅"
        userLabel={user?.userDetails}
        roleLabel={roleLabel}
        density="compact"
        searchEnabled={!isTaskDetailMode && !isMobileCreateMode}
        searchQuery={searchQuery}
        searchPlaceholder="Search by task #, title, category, project, or assignee"
        onSearchChange={setSearchQuery}
        sortEnabled={!isTaskDetailMode && !isMobileCreateMode}
        sortBy={sortBy}
        sortDirection={sortDirection}
        sortOptions={[
          { value: 'urgency', label: 'Urgency' },
          { value: 'number', label: 'Task #' },
          { value: 'due', label: 'Due date' },
          { value: 'remaining', label: 'Remaining days' },
          { value: 'workflow', label: 'RFI workflow' },
          { value: 'assigned', label: 'Assigned to me' },
          { value: 'created', label: 'Created by me' },
          { value: 'priority', label: 'Priority' },
          { value: 'status', label: 'Task status' },
        ]}
        onSortChange={handleSortChange}
        onSortDirectionToggle={toggleSortDirection}
        filters={!isTaskDetailMode && !isMobileCreateMode ? (
          <div className="task-filter-controls">
            <div className="task-filter-controls__select">
              <Select
                value={statusFilter}
                fullWidth
                options={statusFilterOptions}
                onChange={(e) => setStatusFilter(e.target.value as TaskStatusFilter)}
              />
            </div>
            <label className="task-filter-toggle" htmlFor="task-show-completed-toggle">
              <input
                id="task-show-completed-toggle"
                type="checkbox"
                checked={showCompletedTasks}
                onChange={(e) => handleCompletedVisibilityChange(e.target.checked)}
              />
              <span>{isNarrowViewport ? 'Completed' : 'Show completed'}</span>
            </label>
            <label className="task-filter-toggle" htmlFor="task-oldest-first-toggle">
              <input
                id="task-oldest-first-toggle"
                type="checkbox"
                checked={oldestFirst}
                onChange={(e) => handleOldestFirstChange(e.target.checked)}
              />
              <span>{isNarrowViewport ? 'Oldest' : 'Oldest first'}</span>
            </label>
            <div className="task-filter-controls__date">
              <Input
                type="date"
                density="compact"
                fullWidth
                value={dateFrom}
                onChange={(e) => setDateFrom(normalizeDateOnlyInput(e.target.value))}
              />
            </div>
            <div className="task-filter-controls__date">
              <Input
                type="date"
                density="compact"
                fullWidth
                value={dateTo}
                onChange={(e) => setDateTo(normalizeDateOnlyInput(e.target.value))}
              />
            </div>
            {hasActiveDateRange ? (
              <div className="task-filter-controls__clear">
                <Button
                  variant="secondary"
                  shape="pill"
                  onClick={() => {
                    setDateFrom('');
                    setDateTo('');
                  }}
                >
                  {isNarrowViewport ? 'Clear' : 'Clear Dates'}
                </Button>
              </div>
            ) : null}
          </div>
        ) : undefined}
        actions={isTaskDetailMode
          ? [{
              label: 'Back to Task List',
              onClick: () => navigate('/tasks'),
              variant: 'secondary' as const,
            }]
          : [
          ...(!isMobileCreateMode ? [
            {
              label: reminderOpen ? 'Hide Reminder' : 'Remind',
              onClick: toggleReminderPanel,
              variant: 'secondary' as const,
            },
          ] : []),
          {
            label: formOpen ? (isMobileCreateMode ? 'Close' : 'Hide Form') : 'Add Task',
            onClick: () => {
              setFormNotice(null);
              setFormOpen((current) => !current);
            },
            variant: 'primary',
          },
        ]}
        controlBarDensity="compact"
      />}

      {(aiFocus || hasActiveDateRange) ? (
        <div className="task-banner task-banner--company-ai">
          <Card padding="md">
            <div className="dgf-notice__row">
              <div className="dgf-notice dgf-notice--info" style={{ flex: 1 }}>
                {isUsingAiPeriodRange ? (
                  <>
                    Showing Company AI tasks for <strong>{aiPeriodFilter.label}</strong> ({aiPeriodFilter.startDate} to {aiPeriodFilter.endDate}) by <strong>{aiPeriodFilter.dateBasis || 'due date'}</strong>.
                  </>
                ) : hasActiveDateRange ? (
                  <>
                    Filtering tasks from <strong>{dateFrom || 'any start'}</strong> to <strong>{dateTo || 'any end'}</strong> by <strong>{aiPeriodFilter?.dateBasis || 'due date'}</strong>.
                  </>
                ) : aiFocus && !aiFocus.recordId ? (
                  <>
                    Opened from Company AI for <strong>{aiFocus.title}</strong>. Search and task filters were widened to keep the result list visible.
                  </>
                ) : (
                  <>
                    Focused from Company AI on <strong>{aiFocus?.title}</strong>. Search and task filters were widened so the matched row is easier to find.
                  </>
                )}
              </div>
              {aiFollowUp?.prompt ? (
                <Button
                  variant="secondary"
                  shape="pill"
                  onClick={() => navigate(`/ai?prompt=${encodeURIComponent(aiFollowUp.prompt)}`, {
                    state: {
                      companyAiFollowUp: aiFollowUp,
                    },
                  })}
                >
                  Continue in workspace
                </Button>
              ) : null}
            </div>
          </Card>
        </div>
      ) : null}

      {!isTaskDetailMode && reminderOpen && (
        <div className="task-panel task-panel--compact">
          <div className="task-panel__header">
            <div>
              <div className="task-panel__title">Send task reminder</div>
              <div className="task-panel__subtitle">
                Pick a person and send their current assigned-task list by email with a direct link back to the task page.
              </div>
            </div>
          </div>

          <div className="task-panel__controls">
            <div style={{ flex: isNarrowViewport ? '1 1 100%' : '1 1 300px', minWidth: 0 }}>
              <Select
                label="Person to remind"
                value={reminderRecipientEmail}
                fullWidth
                density="compact"
                options={reminderRecipientOptions.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))}
                onChange={(e) => setReminderRecipientEmail(e.target.value)}
              />
            </div>

            <button
              type="button"
              onClick={handleSendReminder}
              disabled={reminderSending || reminderRecipientOptions.length === 0}
              style={{
                ...(reminderSending || reminderRecipientOptions.length === 0 ? secondaryButtonStyle : primaryButtonStyle),
                cursor: reminderSending || reminderRecipientOptions.length === 0 ? 'not-allowed' : 'pointer',
                minWidth: isNarrowViewport ? '100%' : 'auto',
              }}
            >
              {reminderSending ? 'Sending...' : 'Send Reminder'}
            </button>
          </div>

          {reminderRecipientOptions.length === 0 && (
            <div className="task-feedback task-feedback--warning">
              No assignee emails are available yet.
            </div>
          )}

          {reminderError && (
            <div className="task-feedback task-feedback--danger">{reminderError}</div>
          )}

          {reminderNotice && (
            <div className="task-feedback task-feedback--success">{reminderNotice}</div>
          )}
        </div>
      )}

      {!isTaskDetailMode && formOpen && (
        <form
          onSubmit={handleCreateTask}
          onChangeCapture={() => setFormNotice(null)}
          className="task-panel task-create-panel"
        >
          <div className="task-panel__header">
            <div>
              <div className="task-panel__title">Create a task</div>
              <div className="task-panel__subtitle">
                Civil tasks default to Wilson Du. Structural and to-do tasks default to you.
              </div>
            </div>
            <div className="task-panel__actions task-create-panel__top-actions">
              <button
                type="button"
                onClick={resetForm}
                style={secondaryButtonStyle}
              >
                Reset
              </button>
              <button
                type="submit"
                disabled={saving}
                style={{
                  ...(saving ? secondaryButtonStyle : primaryButtonStyle),
                  cursor: saving ? 'default' : 'pointer',
                }}
              >
                {saving ? 'Saving...' : 'Create Task'}
              </button>
            </div>
          </div>

          <div style={{ marginTop: '12px' }}>
            <FormGrid density="compact" className="task-create-grid">
              <FormGridItem width="short" className="task-create-field task-create-field--category">
                <Select
                  label="Category"
                  density="compact"
                  fullWidth
                  value={form.category}
                  options={TASK_CATEGORY_OPTIONS.map((category) => ({ value: category, label: category }))}
                  onChange={(e) => handleCategoryChange(e.target.value as TaskCategory)}
                />
              </FormGridItem>

              <FormGridItem width="short" className="task-create-field task-create-field--type">
                <Select
                  label="Task Type"
                  density="compact"
                  fullWidth
                  value={form.taskType}
                  options={TASK_TYPE_OPTIONS.map((taskType) => ({ value: taskType, label: taskType }))}
                  onChange={(e) => handleTaskTypeChange(e.target.value as TaskType)}
                />
              </FormGridItem>

              <FormGridItem width="short" className="task-create-field task-create-field--priority">
                <Select
                  label="Priority"
                  density="compact"
                  fullWidth
                  value={form.priority}
                  options={TASK_PRIORITY_OPTIONS.map((priority) => ({ value: priority, label: priority }))}
                  onChange={(e) => setForm((current) => ({ ...current, priority: e.target.value as TaskPriority }))}
                />
              </FormGridItem>

              {(!isNarrowViewport || form.taskType === 'RFI') ? (
                <FormGridItem width="short" className="task-create-field task-create-field--pricing">
                  <Input
                    label="Pricing"
                    density="compact"
                    fullWidth
                    type="number"
                    min="0"
                    step="0.01"
                    iconBefore={<span style={{ color: 'var(--color-primary)', fontWeight: 700 }}>$</span>}
                    value={form.pricingAmount}
                    onChange={(e) => setForm((current) => ({ ...current, pricingAmount: e.target.value }))}
                    placeholder="0.00"
                  />
                </FormGridItem>
              ) : null}

              <FormGridItem width="medium" className="task-create-field task-create-field--assigned">
                <Select
                  label="Assigned"
                  density="compact"
                  fullWidth
                  value={form.assignedToEmail}
                  options={assigneeOptions}
                  onChange={(e) => {
                    setAssigneeDirty(true);
                    setForm((current) => ({ ...current, assignedToEmail: e.target.value }));
                  }}
                />
                <div className="dgf-inline-help" style={{ marginTop: 6 }}>
                  {assigneeOptionsBusy
                    ? 'Loading active users from the database...'
                    : assigneeOptionsMessage || 'Assignees are loaded from the active user table.'}
                </div>
              </FormGridItem>

              <FormGridItem width="date" className="task-create-field task-create-field--date">
                <Input
                  label="Start Date"
                  density="compact"
                  fullWidth
                  type="date"
                  value={form.startDate}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                />
              </FormGridItem>

              <FormGridItem width="date" className="task-create-field task-create-field--date">
                <Input
                  label="Due Date"
                  density="compact"
                  fullWidth
                  type="date"
                  value={form.dueDate}
                  onChange={(e) => setForm((current) => ({ ...current, dueDate: e.target.value }))}
                  helpText={form.taskType === 'RFI' ? 'Defaults to 2 days from start date.' : 'Defaults to 7 days from start date.'}
                />
              </FormGridItem>

              {(!isNarrowViewport || form.taskType === 'RFI') ? (
                <FormGridItem width="short" className="task-create-field task-create-field--rfi-ticket">
                  <Input
                    label="RFI Ticket"
                    density="compact"
                    fullWidth
                    type="text"
                    value={form.rfiTicketNumber}
                    onChange={(e) => setForm((current) => ({ ...current, rfiTicketNumber: e.target.value.toUpperCase() }))}
                    placeholder={form.taskType === 'RFI' ? 'Auto-generated if blank' : 'Optional'}
                    disabled={form.taskType !== 'RFI'}
                  />
                </FormGridItem>
              ) : null}

              {(!isNarrowViewport || form.taskType === 'RFI') ? (
                <FormGridItem width="medium" className="task-create-field task-create-field--responding-email">
                  <Input
                    label="Responding Email"
                    density="compact"
                    fullWidth
                    type="email"
                    value={form.responseEmail}
                    onChange={(e) => setForm((current) => ({ ...current, responseEmail: e.target.value.toLowerCase() }))}
                    placeholder={form.taskType === 'RFI' ? 'Used for invoice sending' : 'Optional'}
                    disabled={form.taskType !== 'RFI'}
                    helpText={form.taskType === 'RFI' ? 'RFI invoice emails will send here first and cc the project client.' : undefined}
                  />
                </FormGridItem>
              ) : null}

              <FormGridItem width="full" className="task-create-field task-create-field--description">
                <Textarea
                  label="Description"
                  density="compact"
                  fullWidth
                  rows={1}
                  value={form.description}
                  onChange={(e) => setForm((current) => ({ ...current, description: e.target.value }))}
                  placeholder="Describe the task clearly so it is easy to track later."
                />
              </FormGridItem>
            </FormGrid>
          </div>

          <div style={{ marginTop: '12px' }}>
            <div className="task-project-search__head">
              <div className="task-project-search__title">{isNarrowViewport ? 'Project' : 'Linked project'}</div>
              <div className="task-project-search__hint">
                Search manually, or let the description suggest likely projects.
              </div>
            </div>

            <div style={{ position: 'relative', marginTop: '8px' }}>
              <Input
                density="compact"
                fullWidth
                type="text"
                value={projectSearchQuery}
                onChange={(e) => {
                  setProjectSearchQuery(e.target.value);
                  if (selectedProject && e.target.value !== buildProjectSearchValue(selectedProject)) {
                    setSelectedProject(null);
                  }
                }}
                placeholder="Start typing a project number, address, or name"
                style={{
                  ...compactProjectInputStyle,
                  borderColor: (form.category === 'Civil' || form.category === 'Structural') && !selectedProject ? 'rgba(34, 81, 180, 0.3)' : undefined,
                }}
              />

              {projectSearchLoading && (
                <div className="task-feedback">Searching projects...</div>
              )}

              {!projectSearchLoading && !selectedProject && projectSearchQuery.trim().length < 2 && descriptionMatchLoading && (
                <div className="task-feedback">Matching project from description...</div>
              )}

              {projectSearchError && (
                <div className="task-feedback task-feedback--danger">{projectSearchError}</div>
              )}

              {!projectSearchLoading && projectOptions.length > 0 && (
                <div className="task-project-options">
                  {projectOptions.map((project) => (
                    <button
                      key={project.projectId}
                      type="button"
                      onClick={() => {
                        setFormNotice(null);
                        setSelectedProject(project);
                        setProjectSearchQuery(buildProjectSearchValue(project));
                        setProjectOptions([]);
                      }}
                      className="task-project-option"
                    >
                      <div className="task-project-option__title">
                        {project.projectCode || 'Project'} {project.projectName ? `- ${project.projectName}` : ''}
                      </div>
                      <div className="task-project-option__meta">
                        {buildProjectLocationLabel(project.siteAddress, project.suburb, project.description)}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {!projectSearchLoading && !selectedProject && projectSearchQuery.trim().length < 2 && descriptionMatchOptions.length > 0 && (
                <div className="task-project-options">
                  <div className="task-project-options__label">
                    Suggested from description
                  </div>
                  {descriptionMatchOptions.map((project) => (
                    <button
                      key={`match-${project.projectId}`}
                      type="button"
                      onClick={() => {
                        setFormNotice(null);
                        setSelectedProject(project);
                        setProjectSearchQuery(buildProjectSearchValue(project));
                        setProjectOptions([]);
                        setDescriptionMatchOptions([]);
                      }}
                      className="task-project-option"
                    >
                      <div className="task-project-option__title">
                        {project.projectCode || 'Project'} {project.projectName ? `- ${project.projectName}` : ''}
                      </div>
                      <div className="task-project-option__meta">
                        {buildProjectLocationLabel(project.siteAddress, project.suburb, project.description)}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {selectedProject && (
              <div className="task-project-selected">
                <div>
                  <div className="task-project-selected__title">
                    {selectedProject.projectCode || 'Project'} {selectedProject.projectName ? `- ${selectedProject.projectName}` : ''}
                  </div>
                  <div className="task-project-selected__meta">
                    {buildProjectLocationLabel(selectedProject.siteAddress, selectedProject.suburb, selectedProject.description)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setFormNotice(null);
                    setSelectedProject(null);
                    setProjectSearchQuery('');
                  }}
                  style={secondaryButtonStyle}
                >
                  Clear Link
                </button>
              </div>
            )}

            {(form.category === 'Civil' || form.category === 'Structural') && !selectedProject && (
              <div className="task-feedback task-feedback--warning">
                {form.category} tasks usually work best when linked to a project.
              </div>
            )}
          </div>

          <div className="task-create-panel__bottom-actions">
            <button
              type="button"
              onClick={resetForm}
              style={secondaryButtonStyle}
            >
              Reset
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                ...(saving ? secondaryButtonStyle : primaryButtonStyle),
                cursor: saving ? 'default' : 'pointer',
              }}
            >
              {saving ? 'Saving...' : 'Create Task'}
            </button>
          </div>

          {formError && (
            <div className="dgf-notice dgf-notice--danger" style={{ marginTop: '12px' }}>
              {formError}
            </div>
          )}

          {formNotice && (
            <div className="dgf-notice dgf-notice--success" style={{ marginTop: '12px' }}>
              {formNotice}
            </div>
          )}

        </form>
      )}

      {!loading && editError && (
        <div className="task-banner">
          <div className="dgf-notice dgf-notice--danger">{editError}</div>
        </div>
      )}

      {!loading && editNotice && (
        <div className="task-banner">
          <div className="dgf-notice dgf-notice--success">
            <div className="dgf-notice__row">
              <span>{editNotice}</span>
              {taskStatusUndoNotice && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  shape="pill"
                  onClick={handleUndoCompletedStatusChange}
                  loading={quickUpdateTaskId === taskStatusUndoNotice.taskId}
                >
                  Undo
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="dgf-page__content--wide">
          <div className="dgf-state-card">
            <div className="dgf-state-card__icon">⏳</div>
            <div>Loading tasks...</div>
          </div>
        </div>
      )}

      {error && (
        <div className="dgf-page__content--wide">
          <div className="dgf-state-card">
            <div className="dgf-state-card__icon">⚠️</div>
            <div className="dgf-state-card__title">Error loading tasks</div>
            <div className="dgf-notice dgf-notice--danger" style={{ marginTop: '16px' }}>{error}</div>
          <button
            onClick={() => {
              loadTasks().catch(() => undefined);
            }}
            style={{ ...primaryButtonStyle, marginTop: '16px' }}
          >
            Retry
          </button>
        </div>
        </div>
      )}

      {!loading && visibleTasks.length > 0 && (
        <div className="task-list-shell">
          {visibleTasks.map((task) => {
            const projectSummary = buildTaskProjectSummary(task);
            const projectChips = buildTaskProjectChips(task);
            const headline = String(task.title || task.description || 'Untitled task').trim();
            const secondaryDescription = normalizeComparableText(task.description) !== normalizeComparableText(headline)
              ? task.description
              : '';
            const isEditingTask = editingTaskId === task.id && editForm !== null;
            const assignedToLabel = resolveTaskAssigneeLabel(task, isEditingTask && editForm ? editForm.assignedToEmail : null);
            const createdByLabel = formatTaskPersonLabel(task.createdBy || '') || 'Unknown';
            const lastUpdated = task.modifiedDate ? formatDate(task.modifiedDate) : 'Unknown';
            const projectLocation = String(task.projectLocation || '').trim();
            const showProjectLocation = projectLocation && !isDuplicateProjectText(projectLocation, projectChips);
            const isQuickUpdating = quickUpdateTaskId === task.id;
            const isGeneratingInvoice = generatingInvoiceTaskId === task.id;
            const isDeletingTask = deletingTaskId === task.id;
            const isAiFocusTarget = matchesCompanyAiFocusRecord(aiFocus, task.id);
            const quickCategoryValue = isEditingTask && editForm ? editForm.category : normalizeTaskCategory(task.category);
            const quickPriorityValue = isEditingTask && editForm ? editForm.priority : normalizeTaskPriority(task.priority);
            const quickStatusValue = isEditingTask && editForm ? editForm.status : normalizeTaskStatus(task.status);
            const taskTypeValue = isEditingTask && editForm ? editForm.taskType : normalizeTaskType(task.taskType);
            const categoryColors = getCategoryColor(quickCategoryValue);
            const priorityTone = getPriorityTone(quickPriorityValue);
            const statusTone = getStatusTone(quickStatusValue);
            const timelineDisplay = getTaskTimelineDisplay(task);
            const pricingLabel = formatTaskMoney(task.pricingAmount, task.pricingCurrency);
            return (
              <React.Fragment key={task.id}>
                {isTaskDetailMode ? (!isEditingTask ? (
                  <FormSection title="Task Information" icon={<ClipboardList size={20} aria-hidden="true" />}
                    id={getCompanyAiFocusDomId('tasks', task.id) || undefined}
                    className={isAiFocusTarget ? 'dgf-row-focus-target' : undefined}
                    tabIndex={isAiFocusTarget ? -1 : undefined}>
                    <DetailFieldList className="ui-detail-field-list--inline task-detail-fields" fields={[
                      { label: 'Task Number', value: task.taskNumber },
                      { label: 'Task Name', value: headline, wrap: true },
                      ...(secondaryDescription ? [{ label: 'Description', value: secondaryDescription, wrap: true }] : []),
                      { label: 'Status', value: <StatusBadge status={quickStatusValue} /> },
                      { label: 'Task Type', value: taskTypeValue },
                      { label: 'Category', value: (
                        <select aria-label="Task category" value={quickCategoryValue}
                          onChange={(e) => handleQuickCategoryChange(task, e.target.value as TaskCategory)}
                          disabled={isQuickUpdating || editSaving || isDeletingTask}
                          style={{ ...taskQuickChipSelectBaseStyle, minHeight: 44, width: '100%', maxWidth: 180, border: `1px solid ${categoryColors.color}26`, backgroundColor: categoryColors.background, color: categoryColors.color }}>
                          {TASK_CATEGORY_OPTIONS.map((category) => <option key={category} value={category}>{category}</option>)}
                        </select>
                      ) },
                      { label: 'Priority', value: (
                        <select aria-label="Task priority" value={quickPriorityValue}
                          onChange={(e) => handleQuickPriorityChange(task, e.target.value as TaskPriority)}
                          disabled={isQuickUpdating || editSaving || isDeletingTask}
                          style={{ ...taskQuickChipSelectBaseStyle, minHeight: 44, width: '100%', maxWidth: 180, border: `1px solid ${priorityTone.color}26`, backgroundColor: priorityTone.background, color: priorityTone.color }}>
                          {TASK_PRIORITY_OPTIONS.map((priority) => <option key={priority} value={priority}>{priority}</option>)}
                        </select>
                      ) },
                      { label: 'Start Date', value: formatDate(task.startDate) },
                      { label: 'Due / Completed', value: <span style={{ color: timelineDisplay.color }}>{timelineDisplay.headline} · {timelineDisplay.dateLabel}</span> },
                      { label: 'Project', value: projectChips.join(' • ') || 'No linked project', wrap: true },
                      ...(showProjectLocation ? [{ label: 'Project Location', value: projectLocation, wrap: true }] : []),
                      { label: 'Assigned To', value: assignedToLabel, wrap: true },
                      { label: 'Created By', value: createdByLabel, wrap: true },
                      { label: 'Last Updated', value: lastUpdated },
                      ...(pricingLabel ? [{ label: 'Price', value: pricingLabel }] : []),
                      ...(task.rfiTicketNumber ? [{ label: 'RFI Ticket', value: task.rfiTicketNumber }] : []),
                      ...(task.linkedInvoiceNumber ? [{ label: 'Linked Invoice', value: task.linkedInvoiceNumber }] : []),
                      ...(task.invoiceStatus ? [{ label: 'Invoice Status', value: task.invoiceStatus }] : []),
                      ...(task.invoicePaidDate ? [{ label: 'Invoice Paid', value: formatDate(task.invoicePaidDate) }] : []),
                      ...(task.paymentStatus ? [{ label: 'Payment Status', value: task.paymentStatus }] : []),
                    ]} />
                  </FormSection>
                ) : null) : <div
                  id={getCompanyAiFocusDomId('tasks', task.id) || undefined}
                  className={isEditingTask
                    ? isAiFocusTarget
                      ? 'task-row-card task-row-card--editing dgf-row-focus-target'
                      : 'task-row-card task-row-card--editing'
                    : isAiFocusTarget
                      ? 'task-row-card dgf-row-focus-target'
                      : 'task-row-card'}
                  tabIndex={isAiFocusTarget ? -1 : undefined}
                  style={{
                    gridTemplateColumns: isNarrowViewport ? 'minmax(0, 1fr)' : '110px minmax(0, 1fr) auto',
                    gridTemplateAreas: isNarrowViewport
                      ? '"timeline" "controls" "content"'
                      : '"timeline content controls"',
                  }}
                >
                  <div className="task-row-card__timeline">
                    {isNarrowViewport ? (
                      <div className="task-row-card__mobile-timeline">
                        <span className="task-row-card__number">{task.taskNumber}</span>
                        <span>{task.startDate ? `Start ${formatDate(task.startDate)}` : 'Start not set'}</span>
                        <span style={{ color: timelineDisplay.color }}>{timelineDisplay.headline}</span>
                        <span>{timelineDisplay.dateLabel}</span>
                      </div>
                    ) : (
                      <>
                        <div className="task-row-card__number">{task.taskNumber}</div>
                        <div className="task-row-card__micro">
                          {task.startDate ? `Start ${formatDate(task.startDate)}` : 'Start not set'}
                        </div>
                        <div style={{ marginTop: '6px', fontSize: '11px', fontWeight: 700, color: timelineDisplay.color, lineHeight: 1.2 }}>
                          {timelineDisplay.headline}
                        </div>
                        <div className="task-row-card__micro">
                          {timelineDisplay.dateLabel}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="task-row-card__content">
                    <div className="task-row-card__headline-line">
                      <div className="task-row-card__headline">{headline}</div>
                      {task.linkedInvoiceNumber ? (
                        <button
                          type="button"
                          className="task-row-card__invoice-link"
                          onClick={() => openLinkedInvoice(task)}
                          disabled={isQuickUpdating || editSaving || isGeneratingInvoice || isDeletingTask}
                        >
                          Linked invoice {task.linkedInvoiceNumber}
                          {task.invoicePaidDate ? ` • paid ${formatDate(task.invoicePaidDate)}` : ''}
                        </button>
                      ) : null}
                    </div>
                    {secondaryDescription && (
                      <div className="task-row-card__secondary">
                        {secondaryDescription}
                      </div>
                    )}

                    <div className="task-row-card__chips">
                      <span
                        style={{
                            fontSize: '10px',
                            fontWeight: 700,
                            color: taskTypeValue === 'RFI' ? '#9A5713' : '#475569',
                            backgroundColor: taskTypeValue === 'RFI' ? 'var(--color-warning-soft)' : 'var(--color-surface2)',
                          padding: '2px 7px',
                          borderRadius: '999px',
                            border: taskTypeValue === 'RFI' ? '1px solid rgba(192, 108, 22, 0.2)' : '1px solid var(--color-border)',
                        }}
                      >
                        {taskTypeValue}
                      </span>
                      {pricingLabel ? (
                        <span
                          style={{
                            fontSize: '10px',
                            fontWeight: 700,
                            color: '#0F8A63',
                            backgroundColor: 'var(--color-success-soft)',
                            padding: '2px 7px',
                            borderRadius: '999px',
                            border: '1px solid rgba(15, 138, 99, 0.2)',
                          }}
                        >
                          {pricingLabel}
                        </span>
                      ) : null}
                      {task.rfiTicketNumber ? (
                        <span
                          style={{
                            fontSize: '10px',
                            fontWeight: 700,
                            color: '#9A5713',
                            backgroundColor: 'var(--color-warning-soft)',
                            padding: '2px 7px',
                            borderRadius: '999px',
                            border: '1px solid rgba(192, 108, 22, 0.2)',
                          }}
                        >
                          {task.rfiTicketNumber}
                        </span>
                      ) : null}
                      {task.invoiceStatus ? (
                        <span
                          style={{
                            fontSize: '10px',
                            fontWeight: 700,
                            color: task.invoiceStatus.toLowerCase() === 'paid' ? '#0F8A63' : '#2251B4',
                            backgroundColor: task.invoiceStatus.toLowerCase() === 'paid' ? 'var(--color-success-soft)' : 'var(--color-primary-soft)',
                            padding: '2px 7px',
                            borderRadius: '999px',
                            border: task.invoiceStatus.toLowerCase() === 'paid' ? '1px solid rgba(15, 138, 99, 0.2)' : '1px solid rgba(34, 81, 180, 0.2)',
                          }}
                        >
                          Invoice {task.invoiceStatus}
                        </span>
                      ) : null}
                      {task.paymentStatus ? (
                        <span
                          style={{
                            fontSize: '10px',
                            fontWeight: 700,
                            color: isPaidRfiTask(task) ? '#0F8A63' : '#9A5713',
                            backgroundColor: isPaidRfiTask(task) ? 'var(--color-success-soft)' : 'var(--color-warning-soft)',
                            padding: '2px 7px',
                            borderRadius: '999px',
                            border: isPaidRfiTask(task) ? '1px solid rgba(15, 138, 99, 0.2)' : '1px solid rgba(192, 108, 22, 0.2)',
                          }}
                        >
                          Payment {task.paymentStatus}
                        </span>
                      ) : null}
                      {projectChips.length > 0 ? projectChips.map((chip) => (
                        <span
                          key={`${task.id}-${chip}`}
                          style={{
                            fontSize: '10px',
                            fontWeight: 700,
                            color: 'var(--color-text-muted)',
                            backgroundColor: 'var(--color-surface2)',
                            padding: '2px 7px',
                            borderRadius: '999px',
                            border: '1px solid var(--color-border)',
                          }}
                        >
                          {chip}
                        </span>
                      )) : (
                        <span style={{
                          fontSize: '10px',
                          fontWeight: 700,
                          color: 'var(--color-text-muted)',
                          backgroundColor: 'var(--color-surface2)',
                          padding: '2px 7px',
                          borderRadius: '999px',
                          border: '1px dashed var(--color-border)',
                        }}>
                          No linked project
                        </span>
                      )}
                    </div>

                    {showProjectLocation && (
                      <div className="task-row-card__micro" style={{ marginTop: '6px', fontSize: '11px', lineHeight: 1.3 }}>
                        {projectLocation}
                      </div>
                    )}

                    <div
                      style={{
                        marginTop: '6px',
                        fontSize: '11px',
                        color: 'var(--color-text-muted)',
                        lineHeight: 1.3,
                        overflowWrap: 'anywhere',
                      }}
                    >
                      <span>
                        {isNarrowViewport
                          ? `Assigned to ${assignedToLabel} • Updated ${lastUpdated}`
                          : `Assigned to ${assignedToLabel} • Created by ${createdByLabel} • Last updated ${lastUpdated}`}
                      </span>
                    </div>
                    {!showProjectLocation && projectSummary && projectChips.length === 0 && (
                      <div className="task-row-card__micro" style={{ marginTop: '4px', fontSize: '11px', lineHeight: 1.3 }}>
                        {projectSummary}
                      </div>
                    )}
                  </div>

                  <div className="task-row-card__control-panel" style={{ textAlign: isNarrowViewport ? 'left' : 'right', alignItems: isNarrowViewport ? 'stretch' : 'flex-end' }}>
                    <div
                      className="task-row-card__quick-controls"
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: isNarrowViewport ? 'flex-start' : 'flex-end',
                        gap: isNarrowViewport ? '8px' : '6px',
                      }}
                    >
                      <select
                        value={quickCategoryValue}
                        onChange={(e) => handleQuickCategoryChange(task, e.target.value as TaskCategory)}
                        disabled={isQuickUpdating || editSaving || isDeletingTask}
                        style={{
                          ...taskQuickChipSelectBaseStyle,
                          width: isNarrowViewport ? '92px' : '90px',
                          border: `1px solid ${categoryColors.color}26`,
                          backgroundColor: categoryColors.background,
                          color: categoryColors.color,
                        }}
                      >
                        {TASK_CATEGORY_OPTIONS.map((category) => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                      <select
                        value={quickPriorityValue}
                        onChange={(e) => handleQuickPriorityChange(task, e.target.value as TaskPriority)}
                        disabled={isQuickUpdating || editSaving || isDeletingTask}
                        style={{
                          ...taskQuickChipSelectBaseStyle,
                          width: isNarrowViewport ? '86px' : '90px',
                          border: `1px solid ${priorityTone.color}26`,
                          backgroundColor: priorityTone.background,
                          color: priorityTone.color,
                        }}
                      >
                        {TASK_PRIORITY_OPTIONS.map((priority) => (
                          <option key={priority} value={priority}>{priority}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => void handleQuickStatusChange(task, 'Completed')}
                        disabled={quickStatusValue === 'Completed' || isQuickUpdating || editSaving || isDeletingTask}
                        aria-label={quickStatusValue === 'Completed' ? `${task.taskNumber} is completed` : `Mark ${task.taskNumber} completed`}
                        title={quickStatusValue === 'Completed' ? 'Completed' : 'Mark completed'}
                        style={{
                          ...taskQuickChipSelectBaseStyle,
                          width: isNarrowViewport ? '112px' : '120px',
                          border: `1px solid ${statusTone.color}26`,
                          backgroundColor: statusTone.background,
                          color: statusTone.color,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '5px',
                          cursor: quickStatusValue === 'Completed' || isQuickUpdating || editSaving || isDeletingTask ? 'default' : 'pointer',
                        }}
                      >
                        <Check size={12} strokeWidth={2.6} aria-hidden="true" />
                        <span>{isQuickUpdating ? 'Updating...' : quickStatusValue}</span>
                      </button>
                    </div>
                    <div
                      className="task-row-card__row-actions"
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: isNarrowViewport ? 'flex-start' : 'flex-end',
                        gap: isNarrowViewport ? '8px' : '6px',
                      }}
                    >
                      <div className="task-row-card__workflow-actions">
                        {taskTypeValue === 'RFI' ? (
                          task.linkedInvoiceId || task.linkedInvoiceNumber ? (
                            <button
                              type="button"
                              onClick={() => openLinkedInvoice(task)}
                              disabled={isQuickUpdating || editSaving || isGeneratingInvoice || isDeletingTask}
                              style={{
                                width: isNarrowViewport ? 'auto' : 'fit-content',
                                flex: isNarrowViewport ? '1 1 128px' : undefined,
                                ...subtleInfoButtonStyle,
                                cursor: isQuickUpdating || editSaving || isGeneratingInvoice || isDeletingTask ? 'default' : 'pointer',
                              }}
                            >
                              Open Invoice
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => void handleGenerateInvoice(task)}
                              disabled={isQuickUpdating || editSaving || isGeneratingInvoice || isDeletingTask}
                              style={{
                                width: isNarrowViewport ? 'auto' : 'fit-content',
                                flex: isNarrowViewport ? '1 1 148px' : undefined,
                                ...warningButtonStyle,
                                cursor: isQuickUpdating || editSaving || isGeneratingInvoice || isDeletingTask ? 'default' : 'pointer',
                              }}
                            >
                              {isGeneratingInvoice ? 'Generating...' : 'Generate Invoice'}
                            </button>
                          )
                        ) : null}
                      </div>
                      <div className="task-row-card__primary-actions">
                        {String(task.assignedToEmail || '').trim().toLowerCase() === WILSON_EMAIL ? (
                          <button
                            type="button"
                            className="task-row-card__icon-action"
                            onClick={() => setEmailTarget({
                              entityType: 'task',
                              entityId: Number(task.id),
                              label: task.taskNumber || `Task ${task.id}`,
                            })}
                            disabled={isQuickUpdating || editSaving || isGeneratingInvoice || isDeletingTask}
                            aria-label={`Send Email for ${task.taskNumber}`}
                            title="Send task email to Wilson"
                            style={{
                              width: 'fit-content',
                              flex: '0 0 auto',
                              ...secondaryButtonStyle,
                              cursor: isQuickUpdating || editSaving || isGeneratingInvoice || isDeletingTask ? 'default' : 'pointer',
                            }}
                          >
                            <Mail size={14} strokeWidth={2.4} aria-hidden="true" />
                            <span>Email</span>
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="task-row-card__icon-action"
                          onClick={() => startEditingTask(task)}
                          disabled={isQuickUpdating || editSaving || isGeneratingInvoice || isDeletingTask}
                          aria-label={isEditingTask ? `Close editor for ${task.taskNumber}` : `Edit ${task.taskNumber}`}
                          title={isEditingTask ? 'Close editor' : 'Edit task'}
                          style={{
                            width: isNarrowViewport ? '32px' : 'fit-content',
                            flex: isNarrowViewport ? '0 0 32px' : undefined,
                            ...(isEditingTask ? subtleInfoButtonStyle : secondaryButtonStyle),
                            cursor: isQuickUpdating || editSaving || isGeneratingInvoice || isDeletingTask ? 'default' : 'pointer',
                            textTransform: 'capitalize',
                          }}
                        >
                          {isEditingTask ? <X size={14} strokeWidth={2.4} aria-hidden="true" /> : <Pencil size={14} strokeWidth={2.4} aria-hidden="true" />}
                          <span className="task-row-card__action-label">{isEditingTask ? 'Close' : 'Edit'}</span>
                        </button>
                        <button
                          type="button"
                          className="task-row-card__icon-action task-row-card__icon-action--danger"
                          onClick={() => void handleDeleteTask(task)}
                          disabled={isQuickUpdating || editSaving || isGeneratingInvoice || isDeletingTask}
                          aria-label={`Delete ${task.taskNumber}`}
                          title="Delete task"
                          style={{
                            width: isNarrowViewport ? '32px' : 'fit-content',
                            flex: isNarrowViewport ? '0 0 32px' : undefined,
                            ...dangerButtonStyle,
                            cursor: isQuickUpdating || editSaving || isGeneratingInvoice || isDeletingTask ? 'default' : 'pointer',
                          }}
                        >
                          <Trash2 size={14} strokeWidth={2.4} aria-hidden="true" />
                          <span className="task-row-card__action-label">{isDeletingTask ? 'Deleting...' : 'Delete'}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>}

                {isEditingTask && editForm && (
                  <div className="task-edit-panel">
                    <div className="task-edit-panel__title">
                      Edit {task.taskNumber}
                    </div>

                    <div style={{ marginTop: '12px' }}>
                      <FormGrid density="compact">
                        <FormGridItem width="short">
                          <Input
                            label="Task Number"
                            density="compact"
                            fullWidth
                            type="text"
                            value={editForm.taskNumber}
                            onChange={(e) => setEditForm((current) => current ? { ...current, taskNumber: e.target.value } : current)}
                            placeholder="T100"
                          />
                        </FormGridItem>

                        <FormGridItem width="long">
                          <Input
                            label="Task Name"
                            density="compact"
                            fullWidth
                            type="text"
                            value={editForm.title}
                            onChange={(e) => setEditForm((current) => current ? { ...current, title: e.target.value } : current)}
                            placeholder="Short task title"
                          />
                        </FormGridItem>

                        <FormGridItem width="short">
                          <Select
                            label="Category"
                            density="compact"
                            fullWidth
                            value={editForm.category}
                            options={TASK_CATEGORY_OPTIONS.map((category) => ({ value: category, label: category }))}
                            onChange={(e) => handleEditCategoryChange(e.target.value as TaskCategory)}
                          />
                        </FormGridItem>

                        <FormGridItem width="short">
                          <Select
                            label="Task Type"
                            density="compact"
                            fullWidth
                            value={editForm.taskType}
                            options={TASK_TYPE_OPTIONS.map((taskType) => ({ value: taskType, label: taskType }))}
                            onChange={(e) => handleEditTaskTypeChange(e.target.value as TaskType)}
                          />
                        </FormGridItem>

                        <FormGridItem width="short">
                          <Select
                            label="Priority"
                            density="compact"
                            fullWidth
                            value={editForm.priority}
                            options={TASK_PRIORITY_OPTIONS.map((priority) => ({ value: priority, label: priority }))}
                            onChange={(e) => setEditForm((current) => current ? { ...current, priority: e.target.value as TaskPriority } : current)}
                          />
                        </FormGridItem>

                        <FormGridItem width="short">
                          <Input
                            label="Pricing"
                            density="compact"
                            fullWidth
                            type="number"
                            min="0"
                            step="0.01"
                            iconBefore={<span style={{ color: 'var(--color-primary)', fontWeight: 700 }}>$</span>}
                            value={editForm.pricingAmount}
                            onChange={(e) => setEditForm((current) => current ? { ...current, pricingAmount: e.target.value } : current)}
                            placeholder="0.00"
                          />
                        </FormGridItem>

                        <FormGridItem width="short">
                          <Input
                            label="RFI Ticket"
                            density="compact"
                            fullWidth
                            type="text"
                            value={editForm.rfiTicketNumber}
                            onChange={(e) => setEditForm((current) => current ? { ...current, rfiTicketNumber: e.target.value.toUpperCase() } : current)}
                            placeholder={editForm.taskType === 'RFI' ? 'Auto-generated if blank' : 'Optional'}
                            disabled={editForm.taskType !== 'RFI'}
                          />
                        </FormGridItem>

                        <FormGridItem width="medium">
                          <Input
                            label="Responding Email"
                            density="compact"
                            fullWidth
                            type="email"
                            value={editForm.responseEmail}
                            onChange={(e) => setEditForm((current) => current ? { ...current, responseEmail: e.target.value.toLowerCase() } : current)}
                            placeholder={editForm.taskType === 'RFI' ? 'Used for invoice sending' : 'Optional'}
                            disabled={editForm.taskType !== 'RFI'}
                            helpText={editForm.taskType === 'RFI' ? 'RFI invoice emails will send here first and cc the project client.' : undefined}
                          />
                        </FormGridItem>

                        <FormGridItem width="medium">
                          <Select
                            label="Assigned"
                            density="compact"
                            fullWidth
                            value={editForm.assignedToEmail}
                            options={assigneeOptions}
                            onChange={(e) => setEditForm((current) => current ? { ...current, assignedToEmail: e.target.value } : current)}
                          />
                          <div className="dgf-inline-help" style={{ marginTop: 6 }}>
                            {assigneeOptionsBusy
                              ? 'Loading active users from the database...'
                              : assigneeOptionsMessage || 'Assignees are loaded from the active user table.'}
                          </div>
                        </FormGridItem>

                        <FormGridItem width="date">
                          <Input
                            label="Start Date"
                            density="compact"
                            fullWidth
                            type="date"
                            value={editForm.startDate}
                            onChange={(e) => handleEditStartDateChange(e.target.value)}
                          />
                        </FormGridItem>

                        <FormGridItem width="date">
                          <Input
                            label="Due Date"
                            density="compact"
                            fullWidth
                            type="date"
                            value={editForm.dueDate}
                            onChange={(e) => setEditForm((current) => current ? { ...current, dueDate: e.target.value } : current)}
                            helpText={editForm.taskType === 'RFI' ? 'Defaults to 2 days from start date.' : 'Defaults to 7 days from start date.'}
                          />
                        </FormGridItem>

                        <FormGridItem width="short">
                          <Select
                            label="Status"
                            density="compact"
                            fullWidth
                            value={editForm.status}
                            options={TASK_STATUS_OPTIONS.map((status) => ({ value: status, label: status }))}
                            onChange={(e) => setEditForm((current) => current ? { ...current, status: e.target.value as TaskStatus } : current)}
                          />
                        </FormGridItem>

                        <FormGridItem width="full">
                          <Textarea
                            label="Description"
                            density="compact"
                            fullWidth
                            rows={1}
                            value={editForm.description}
                            onChange={(e) => setEditForm((current) => current ? { ...current, description: e.target.value } : current)}
                            placeholder="Update the task description."
                          />
                        </FormGridItem>
                      </FormGrid>
                    </div>

                    <div style={{ marginTop: '12px' }}>
                      <div className="task-project-search__head">
                        <div className="task-project-search__title">Linked project</div>
                        <div className="task-project-search__hint">
                          Search by project number, address, or name.
                        </div>
                      </div>

                      <div style={{ position: 'relative', marginTop: '8px' }}>
                        <Input
                          density="compact"
                          fullWidth
                          type="text"
                          value={editProjectSearchQuery}
                          onChange={(e) => {
                            setEditProjectSearchQuery(e.target.value);
                            if (editSelectedProject && e.target.value !== buildProjectSearchValue(editSelectedProject)) {
                              setEditSelectedProject(null);
                            }
                          }}
                          placeholder="Start typing a project number, address, or name"
                          style={{
                            ...compactProjectInputStyle,
                            borderColor: (editForm.category === 'Civil' || editForm.category === 'Structural') && !editSelectedProject ? 'rgba(34, 81, 180, 0.3)' : undefined,
                          }}
                        />

                        {editProjectSearchLoading && (
                          <div className="task-feedback">Searching projects...</div>
                        )}

                        {editProjectSearchError && (
                          <div className="task-feedback task-feedback--danger">{editProjectSearchError}</div>
                        )}

                        {!editProjectSearchLoading && editProjectOptions.length > 0 && (
                          <div className="task-project-options">
                            {editProjectOptions.map((project) => (
                              <button
                                key={`edit-${project.projectId}`}
                                type="button"
                                onClick={() => {
                                  setEditSelectedProject(project);
                                  setEditProjectSearchQuery(buildProjectSearchValue(project));
                                  setEditProjectOptions([]);
                                }}
                                className="task-project-option"
                              >
                                <div className="task-project-option__title">
                                  {project.projectCode || 'Project'} {project.projectName ? `- ${project.projectName}` : ''}
                                </div>
                                <div className="task-project-option__meta">
                                  {buildProjectLocationLabel(project.siteAddress, project.suburb, project.description)}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {editSelectedProject && (
                        <div className="task-project-selected">
                          <div>
                            <div className="task-project-selected__title">
                              {editSelectedProject.projectCode || 'Project'} {editSelectedProject.projectName ? `- ${editSelectedProject.projectName}` : ''}
                            </div>
                            <div className="task-project-selected__meta">
                              {buildProjectLocationLabel(editSelectedProject.siteAddress, editSelectedProject.suburb, editSelectedProject.description)}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setEditSelectedProject(null);
                              setEditProjectSearchQuery('');
                            }}
                            style={secondaryButtonStyle}
                          >
                            Clear Link
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="task-panel__actions" style={{ marginTop: '16px' }}>
                      <button
                        type="button"
                        onClick={resetEditState}
                        disabled={editSaving}
                        style={{ ...secondaryButtonStyle, cursor: editSaving ? 'default' : 'pointer' }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSaveTaskEdit(task)}
                        disabled={editSaving}
                        style={{
                          ...(editSaving ? secondaryButtonStyle : primaryButtonStyle),
                          cursor: editSaving ? 'default' : 'pointer',
                        }}
                      >
                        {editSaving ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
          {!isTaskDetailMode && (
            <ListPaginationControls
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalPages={pagination.totalPages}
              totalRecords={pagination.totalRecords}
              currentCount={visibleTasks.length}
              pageSizeOptions={LIST_PAGE_SIZE_OPTIONS}
              onPageChange={setPage}
              onPageSizeChange={(nextPageSize) => {
                setPageSize(nextPageSize);
                setPage(1);
              }}
            />
          )}
        </div>
      )}

      {!loading && visibleTasks.length === 0 && !error && (
        <div style={{ margin: '16px' }}>
          <Card padding="none">
            <EmptyState
              icon={<span style={{ fontSize: '56px' }}>🗂️</span>}
              title={isTaskDetailMode ? 'Task not found' : 'No tasks found'}
              description={isTaskDetailMode
                ? 'This task may have been deleted, or your account may not have access to its project.'
                : searchQuery
                  ? 'Try adjusting the search terms.'
                  : 'Create your first task above to start tracking project work.'}
            />
          </Card>
        </div>
      )}

      <EmailSendModal
        open={Boolean(emailTarget)}
        target={emailTarget}
        onClose={() => setEmailTarget(null)}
      />

      <div style={{ height: '48px' }} />
    </div>
  );
};

export default TaskList;
